import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatMenuTrigger } from "@angular/material/menu";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject, forkJoin, of } from "rxjs";
import {
  catchError,
  debounceTime,
  map,
  switchMap,
  takeUntil,
  tap,
} from "rxjs/operators";
import { AuthService } from "../../auth/auth.service";
import { CompanyService } from "../../company/company.service";
import type { EmployeeWithBrand } from "../../digital-cards/card-digital.interface";
import { DigitalCardService } from "../../digital-cards/digital-card.service";
import { defaultDigitalCardProfileId } from "../../digital-cards/digital-card-profile.util";
import type { DigitalCardProfileItem } from "../../digital-cards/digital-cards.interface";
import type { EmployeePickerItem } from "../../employees/employees.interface";
import { ImagesService } from "../../media/images.service";
import type { Image as MediaImage } from "../../media/media.interface";
import { ConfirmationDialogComponent } from "../../shared/confirmation-dialog/confirmation-dialog.component";
import type { ConfirmationDialogParams } from "../../shared/confirmation-dialog/confirmation-dialog.interface";
import { NameInputDialogComponent } from "../../shared/name-input-dialog/name-input-dialog.component";
import { BRAND_FONT_PRESET_ID } from "../../shared/font-presets";
import { ToastrService } from "../../shared/toastr.service";
import { mediaFilePreview } from "../../shared/utils/media-file-preview.util";
import { TranslateService } from "@ngx-translate/core";
import { SignaturesService } from "../../signatures/signatures.service";
import { PrintCardService } from "../print-card.service";
import {
  addPrintCardElementPositionToAllSizeLayouts,
  clampPrintCardElementBox,
  compilePrintCardDesign,
  compilePrintCardDesignForPageSizes,
  createEmptyPrintCardDesign,
  ensurePrintCardSizeLayouts,
  mergePrintCardSizeLayout,
  migratePrintCardDesignDocument,
  nextUniquePrintCardElementName,
  printCardDefaultTextHeightCm,
  printCardDesignFromLayoutHtml,
  printCardElementDefaultName,
  printCardTextElementToSquashParts,
  removePrintCardElementFromSizeLayouts,
  resetPrintCardActiveSizeLayoutPositions,
  syncPrintCardActiveSizeLayout,
  updatePrintCardElementPositionsInSizeLayouts,
} from "../print-card.util";
import {
  defaultPrintCardPageSizePresetId,
  PRINT_CARD_DEFAULT_FONT_SIZE_BODY_PT,
  PRINT_CARD_DEFAULT_FONT_SIZE_COMPANY_PT,
  PRINT_CARD_DEFAULT_FONT_SIZE_NAME_PT,
  PRINT_CARD_DEFAULT_FONT_SIZE_TAGLINE_PT,
  PRINT_CARD_DEFAULT_FONT_SIZE_TITLE_PT,
  PRINT_CARD_DEFAULT_FONT_WEIGHT_BODY,
  PRINT_CARD_DEFAULT_FONT_WEIGHT_COMPANY,
  PRINT_CARD_DEFAULT_FONT_WEIGHT_NAME,
  PRINT_CARD_DEFAULT_FONT_WEIGHT_TAGLINE,
  PRINT_CARD_DEFAULT_FONT_WEIGHT_TITLE,
  PRINT_CARD_DEFAULT_LOGO_MAX_HEIGHT_CM,
  PRINT_CARD_DEFAULT_LOGO_MAX_WIDTH_CM,
  PRINT_CARD_DEFAULT_NFC_COLOR,
  PRINT_CARD_DEFAULT_NFC_SIZE_CM,
  PRINT_CARD_DEFAULT_PAGE_SIZE_PRESET_ID,
  PRINT_CARD_DEFAULT_QR_SIZE_CM,
  PRINT_CARD_DEFAULT_SIDE_BACKGROUND,
  PRINT_CARD_DEFAULT_SIDE_TEXT,
  PRINT_CARD_DEFAULT_SQUASH_SEPARATOR,
  PRINT_CARD_DEFAULT_TAGLINE_TEXT,
  PRINT_CARD_DOWNLOAD_BLEED_MM,
  PRINT_CARD_FONT_PRESETS,
  PRINT_CARD_ALLOWED_QUANTITIES_MAX,
  PRINT_CARD_MULTILOFT_QUANTITY_OPTIONS,
  PRINT_CARD_PAPER_QUANTITY_OPTIONS,
  PRINT_CARD_PAPER_SAFE_ZONE_INSET_MM,
  PRINT_CARD_PLASTIC_QUANTITY_MAX,
  PRINT_CARD_PLASTIC_QUANTITY_MIN,
  PRINT_CARD_TEMPLATE_TYPES,
  printCardDesignPageFromPreset,
  printCardPageSizePresetsForTemplate,
  type PrintCardDesignDocument,
  type PrintCardElement,
  type PrintCardElementType,
  type PrintCardFontPreset,
  type PrintCardFontWeight,
  type PrintCardLayoutBackStyle,
  type PrintCardLayoutBrandSide,
  type PrintCardLayoutCatalogItem,
  type PrintCardLayoutMarker,
  type PrintCardLayoutVariant,
  type PrintCardOrientation,
  type PrintCardPageSizePreset,
  type PrintCardPrinterSettingsResponse,
  type PrintCardPrintSides,
  type PrintCardSideDesign,
  type PrintCardTemplateType,
  type PrintCardTextElement,
} from "../print-cards.interface";

const DOMAIN_VARIABLE_EXCLUDE = new Set([
  "userName",
  "jobTitle",
  "phone",
  "email",
  "companyName",
  "companyAddress",
  "tagline",
  "custom",
  "squash",
  "companyImage",
  "companyImageUrl",
  "userThumbnail",
  "userThumbnailUrl",
  "QRdigitalCard",
  "QRdigitalCardUrl",
  "QRdigitalCardLink",
  "nfcIcon",
  "addonBanner",
  "addonAdditionalBanner",
  "addonLink",
  "addonDisclaimer",
  "userLinks",
  "userHandwritten",
  "userPlannedAbsence",
  "labelMobileNumber",
  "labelPhoneNumber",
  "labelUserEmail",
  "labelCustom",
]);

const MAX_HISTORY = 50;
const FRONT_BRAND_SIDE_ORDER: PrintCardLayoutBrandSide[] = [
  "right",
  "left",
  "stack-left",
];
const BACK_STYLE_ORDER: PrintCardLayoutBackStyle[] = [
  "blank",
  "tagline",
  "brand",
];
const MARKER_ORDER: PrintCardLayoutMarker[] = ["none", "nfc", "qr"];
const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

@Component({
  selector: "app-print-card-visual-editor",
  templateUrl: "./print-card-visual-editor.component.html",
  styleUrls: ["./print-card-visual-editor.component.scss"],
  standalone: false,
})
export class PrintCardVisualEditorComponent implements OnInit, OnDestroy {
  readonly templateTypes = PRINT_CARD_TEMPLATE_TYPES;
  private readonly builtInFontPresets: PrintCardFontPreset[] =
    PRINT_CARD_FONT_PRESETS;
  readonly paletteGroups: Array<{
    titleKey: string;
    items: Array<{
      labelKey: string;
      type: PrintCardElementType;
      variable?: string;
      pickDomainVariable?: boolean;
    }>;
  }> = [
    {
      titleKey: "printCards.paletteGroupVariables",
      items: [
        {
          labelKey: "printCards.paletteName",
          type: "text",
          variable: "userName",
        },
        {
          labelKey: "printCards.paletteTitle",
          type: "text",
          variable: "jobTitle",
        },
        {
          labelKey: "printCards.palettePhone",
          type: "text",
          variable: "phone",
        },
        {
          labelKey: "printCards.paletteEmail",
          type: "text",
          variable: "email",
        },
        {
          labelKey: "printCards.paletteCompany",
          type: "text",
          variable: "companyName",
        },
        {
          labelKey: "printCards.paletteAddress",
          type: "text",
          variable: "companyAddress",
        },
        {
          labelKey: "printCards.paletteTagline",
          type: "text",
          variable: "tagline",
        },
        {
          labelKey: "printCards.paletteText",
          type: "text",
          variable: "custom",
        },
        {
          labelKey: "printCards.paletteVariable",
          type: "text",
          pickDomainVariable: true,
        },
      ],
    },
    {
      titleKey: "printCards.paletteGroupMedia",
      items: [
        { labelKey: "printCards.paletteLogo", type: "logo" },
        { labelKey: "printCards.paletteImage", type: "image" },
        { labelKey: "printCards.paletteQr", type: "qr" },
        { labelKey: "printCards.paletteNfc", type: "nfc" },
      ],
    },
    {
      titleKey: "printCards.paletteGroupOther",
      items: [{ labelKey: "printCards.paletteShape", type: "shape" }],
    },
  ];

  pageReady = false;
  saving = false;
  showLayoutStart = true;
  loadingThumbs = true;
  loadingPreview = true;
  previewEmployeeLoading = true;
  employeePickerReady = false;
  printCardTemplateId: string | null = null;
  readonly editorPanelOpen: Record<
    "elements" | "layers" | "orderOptions" | "printerOptions",
    boolean
  > = {
    elements: true,
    layers: true,
    orderOptions: false,
    printerOptions: false,
  };

  toggleEditorPanel(
    key: "elements" | "layers" | "orderOptions" | "printerOptions",
  ): void {
    this.editorPanelOpen[key] = !this.editorPanelOpen[key];
  }
  get selectedTemplateType(): PrintCardTemplateType {
    return this.form.controls.templateType.value;
  }

  get selectedPageSizePresetId(): string {
    return this.form.controls.pageSizePreset.value;
  }

  get selectedOrientation(): PrintCardOrientation {
    return this.form.controls.orientation.value;
  }

  readonly orientations: PrintCardOrientation[] = ["landscape", "portrait"];

  get activeSafeZoneInsetMm(): number {
    const preset =
      this.orderPageSizePresets.find(
        (item) => item.id === this.selectedPageSizePresetId,
      ) ?? this.orderPageSizePresets[0];
    return preset?.safeZoneInsetMm ?? PRINT_CARD_PAPER_SAFE_ZONE_INSET_MM;
  }

  get selectedFontId(): string {
    return this.form.controls.fontPreset.value;
  }

  get companyBrandFontReady(): boolean {
    return (
      this.companyFontFamily.trim().length > 0 &&
      (this.companyFontFileUrl?.trim().length ?? 0) > 0
    );
  }

  get fontPresets(): PrintCardFontPreset[] {
    if (!this.companyBrandFontReady) {
      return this.builtInFontPresets;
    }
    return [
      {
        id: BRAND_FONT_PRESET_ID,
        labelKey: "printCards.fontUploaded",
        label: this.companyFontFamily.trim(),
        fontFamily: `"${this.companyFontFamily.replace(/"/g, "")}"`,
        fontCssUrl: null,
      },
      ...this.builtInFontPresets,
    ];
  }

  layouts: PrintCardLayoutCatalogItem[] = [];
  pageSizePresets: PrintCardPageSizePreset[] = [];
  layoutThumbs: Record<string, string> = {};
  printerSettings: PrintCardPrinterSettingsResponse | null = null;
  previewEmployee: EmployeePickerItem | null = null;
  previewEmployeeData: EmployeeWithBrand | null = null;
  previewFrontHtml: string | null = null;
  previewBackHtml: string | null = null;
  digitalCardTemplates: DigitalCardProfileItem[] = [];
  readonly digitalCardTemplateLabel = (template: DigitalCardProfileItem) =>
    template.displayName || template.profileId || "";
  readonly digitalCardTemplateValue = (template: DigitalCardProfileItem) =>
    template.profileId;
  companyFontFamily = "";
  companyFontFileUrl: string | null = null;
  companyPreviewName = "";
  companyPreviewAddress = "";
  companyPreviewImageUrl = "";
  domainVariables: string[] = [
    "userGivenName",
    "userFamilyName",
    "userAbout",
    "userDepartment",
    "companyWebsite",
    "companyPhone",
    "contactPersonEmail",
  ];

  selectedLayoutKey: string | null = null;
  selectedBrandSide: PrintCardLayoutBrandSide = "right";
  selectedFrontMarker: PrintCardLayoutMarker = "none";
  selectedBackStyle: PrintCardLayoutBackStyle = "blank";
  selectedBackMarker: PrintCardLayoutMarker = "none";
  layoutApplied = false;
  setupFrontHtml = "";
  setupBackHtml = "";
  frontTextColor = PRINT_CARD_DEFAULT_SIDE_TEXT;
  backTextColor = PRINT_CARD_DEFAULT_SIDE_TEXT;
  frontBackgroundColor = PRINT_CARD_DEFAULT_SIDE_BACKGROUND;
  backBackgroundColor = PRINT_CARD_DEFAULT_SIDE_BACKGROUND;
  frontNfcColor = PRINT_CARD_DEFAULT_NFC_COLOR;
  backNfcColor = PRINT_CARD_DEFAULT_NFC_COLOR;
  mediaImages: MediaImage[] = [];
  mediaImagesLoading = false;
  contextMenuPosition = { x: 0, y: 0 };
  readonly plasticQuantityMin = PRINT_CARD_PLASTIC_QUANTITY_MIN;
  readonly plasticQuantityMax = PRINT_CARD_PLASTIC_QUANTITY_MAX;
  readonly plasticQuantityAddSentinel = -1;
  plasticQuantityOptions: number[] = [];
  plasticQuantityDraft: number | null = null;

  @ViewChild("elementContextMenuTrigger", { read: MatMenuTrigger })
  elementContextMenuTrigger: MatMenuTrigger | null = null;

  document: PrintCardDesignDocument = createEmptyPrintCardDesign({
    widthCm: 9,
    heightCm: 5,
    bleedMm: PRINT_CARD_DOWNLOAD_BLEED_MM,
    safeZoneInsetMm: PRINT_CARD_PAPER_SAFE_ZONE_INSET_MM,
  });
  activeSide: "front" | "back" = "front";
  selectedElementId: string | null = null;
  zoom = 1;

  form = new FormGroup({
    name: new FormControl("", {
      nonNullable: true,
    }),
    templateType: new FormControl<PrintCardTemplateType>("PAPER", {
      nonNullable: true,
    }),
    pageSizePreset: new FormControl(PRINT_CARD_DEFAULT_PAGE_SIZE_PRESET_ID, {
      nonNullable: true,
    }),
    orientation: new FormControl<PrintCardOrientation>("landscape", {
      nonNullable: true,
    }),
    allowedPageSizePresetIds: new FormControl<string[]>([], {
      nonNullable: true,
    }),
    allowedQuantities: new FormControl<number[]>(
      [...PRINT_CARD_PAPER_QUANTITY_OPTIONS],
      { nonNullable: true },
    ),
    fontPreset: new FormControl(PRINT_CARD_FONT_PRESETS[0]?.id ?? "roboto", {
      nonNullable: true,
    }),
    digitalCardProfileId: new FormControl<string | null>(null),
    printerPaperId: new FormControl(0, { nonNullable: true }),
    printSides: new FormControl<PrintCardPrintSides>("4/4", {
      nonNullable: true,
    }),
    printerSpacerColorId: new FormControl(0, { nonNullable: true }),
  });

  private readonly destroy$ = new Subject<void>();
  private readonly previewTick$ = new Subject<void>();
  private readonly thumbsTick$ = new Subject<void>();
  private readonly previewEmployeeEmail$ = new Subject<string>();
  private undoStack: PrintCardDesignDocument[] = [];
  private redoStack: PrintCardDesignDocument[] = [];
  private interactionOpen = false;
  private canvasSampleTextsCache: Partial<Record<string, string>> | null = null;
  private pendingFontSettings: {
    fontName?: string;
    fontFamily?: string;
    fontCssUrl?: string | null;
  } | null = null;
  private syncPageSizeAfterCatalogLoad = false;

  constructor(
    public auth: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private printCardService: PrintCardService,
    private digitalCardService: DigitalCardService,
    private companyService: CompanyService,
    private signaturesService: SignaturesService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private imagesService: ImagesService,
  ) {}

  ngOnInit(): void {
    if (!this.auth.isSuperAdmin) {
      void this.router.navigate(["/no-access"]);
      return;
    }
    this.printCardTemplateId = this.route.snapshot.paramMap.get(
      "printCardTemplateId",
    );
    this.showLayoutStart = !this.printCardTemplateId;
    this.loadMediaImages();
    this.loadCompanySettings();
    this.loadDomainVariables();
    this.previewEmployeeEmail$
      .pipe(
        tap(() => {
          this.previewEmployeeLoading = true;
        }),
        switchMap((email) =>
          email
            ? this.digitalCardService.getPreviewEmployee(email).pipe(
                map((res) => res.employee),
                catchError(() => of<EmployeeWithBrand | null>(null)),
              )
            : of<EmployeeWithBrand | null>(null),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((employee) => {
        this.previewEmployeeData = employee;
        this.previewEmployeeLoading = false;
      });

    this.previewTick$
      .pipe(
        debounceTime(600),
        switchMap(() => {
          if (!this.canRequestPreview) {
            return of(null);
          }
          const email = this.previewEmployee?.email?.trim();
          if (!email) {
            return of(null);
          }
          const font = this.selectedFont;
          const pageSizePresetId =
            this.document.activePageSizePresetId?.trim() ||
            this.selectedPageSizePresetId;
          if (!pageSizePresetId?.trim()) {
            return of(null);
          }
          const compiled = compilePrintCardDesign(
            this.document,
            pageSizePresetId,
          );
          const frontContent = compiled.frontContent;
          const backContent = compiled.backContent;
          if (!frontContent.trim()) {
            return of(null);
          }
          const pageBleedMm = Math.min(
            20,
            Math.max(0, Number(this.document.page.bleedMm) || 0),
          );
          this.loadingPreview = true;
          return this.printCardService
            .previewDraft({
              email,
              type: this.selectedTemplateType,
              frontContent,
              backContent,
              digitalCardProfileId:
                this.form.controls.digitalCardProfileId.value,
              settings: {
                pageSizePresetId,
                pageWidthCm: this.document.page.widthCm,
                pageHeightCm: this.document.page.heightCm,
                pageBleedMm,
                orientation: this.selectedOrientation,
                frontBackgroundColor: this.document.front.backgroundColor,
                backBackgroundColor: this.document.back.backgroundColor,
                frontBackgroundImageUrl:
                  this.document.front.backgroundImageUrl ?? null,
                backBackgroundImageUrl:
                  this.document.back.backgroundImageUrl ?? null,
                frontTextColor: this.frontTextColor,
                backTextColor: this.backTextColor,
                frontNfcColor: this.frontNfcColor,
                backNfcColor: this.backNfcColor,
                fontName: font.fontName,
                fontFamily: font.fontFamily,
                fontCssUrl: font.fontCssUrl,
              },
            })
            .pipe(catchError(() => of(null)));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((res) => {
        this.loadingPreview = false;
        if (!res) {
          return;
        }
        this.previewFrontHtml = res.frontDocumentHtml?.trim() || null;
        this.previewBackHtml = res.backDocumentHtml?.trim() || null;
      });

    this.thumbsTick$
      .pipe(
        debounceTime(600),
        switchMap(() => {
          if (!this.showLayoutStart || !this.layouts.length) {
            return of(null);
          }
          const email = this.previewEmployee?.email?.trim();
          if (!email) {
            return of(null);
          }
          const preset =
            this.orderPageSizePresets.find(
              (item) => item.id === this.selectedPageSizePresetId,
            ) ?? this.orderPageSizePresets[0];
          const orientation = this.selectedOrientation;
          const font = this.selectedFont;
          const page = preset
            ? printCardDesignPageFromPreset(preset, orientation)
            : this.document.page;
          const compiledVariants = this.buildCompiledLayoutVariants(
            page,
            orientation,
          );
          this.loadingThumbs = true;
          return this.printCardService
            .previewLayoutCatalog({
              email,
              type: this.selectedTemplateType,
              digitalCardProfileId:
                this.form.controls.digitalCardProfileId.value,
              compiledVariants,
              settings: {
                pageSizePresetId: preset?.id,
                pageWidthCm: page.widthCm,
                pageHeightCm: page.heightCm,
                pageBleedMm: Math.min(
                  20,
                  Math.max(0, Number(page.bleedMm) || 0),
                ),
                orientation,
                ...(typeof preset?.cornerRadiusMm === "number"
                  ? { cornerRadiusMm: preset.cornerRadiusMm }
                  : {}),
                fontName: font.fontName,
                fontFamily: font.fontFamily,
                fontCssUrl: font.fontCssUrl,
                frontBackgroundColor: this.frontBackgroundColor,
                backBackgroundColor: this.backBackgroundColor,
                frontTextColor: this.frontTextColor,
                backTextColor: this.backTextColor,
                frontNfcColor: this.frontNfcColor,
                backNfcColor: this.backNfcColor,
                frontBackgroundImageUrl:
                  this.document.front.backgroundImageUrl ?? null,
                backBackgroundImageUrl:
                  this.document.back.backgroundImageUrl ?? null,
              },
            })
            .pipe(catchError(() => of({ thumbs: this.layoutThumbs })));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((res) => {
        if (!res?.thumbs) {
          if (
            this.showLayoutStart &&
            this.layouts.length > 0 &&
            !this.previewEmployee?.email?.trim()
          ) {
            this.loadingThumbs = true;
            return;
          }
          this.loadingThumbs = false;
          return;
        }
        this.loadingThumbs = false;
        this.layoutThumbs = res.thumbs;
      });

    forkJoin({
      catalogs: this.printCardService
        .prefetchCatalogs()
        .pipe(catchError(() => of(null))),
      profiles: this.digitalCardService
        .getProfiles({ templatesOnly: true })
        .pipe(
          catchError(() => of({ profiles: [] as DigitalCardProfileItem[] })),
        ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ profiles }) => {
        this.digitalCardTemplates = profiles.profiles ?? [];
        if (
          !this.printCardTemplateId &&
          !this.form.controls.digitalCardProfileId.value?.trim()
        ) {
          const profileId = defaultDigitalCardProfileId(
            this.digitalCardTemplates,
          );
          if (profileId) {
            this.form.patchValue({ digitalCardProfileId: profileId });
          }
        }
        this.startEditor();
      });

    this.form.controls.allowedPageSizePresetIds.valueChanges
      .pipe(debounceTime(0), takeUntil(this.destroy$))
      .subscribe(() => {
        this.syncAllowedSizeLayouts();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get canSave(): boolean {
    return !this.saving && this.layoutApplied;
  }

  get hasPrinterOptions(): boolean {
    return (
      (this.selectedTemplateType === "PAPER" ||
        this.selectedTemplateType === "PAPER_NFC" ||
        this.selectedTemplateType === "PLASTIC_NFC") &&
      (this.printerSettings?.enabled ?? false)
    );
  }

  get isMultiloftPrinterOptions(): boolean {
    return this.selectedTemplateType === "PAPER_NFC" && this.hasPrinterOptions;
  }

  get isPlasticNfcTemplate(): boolean {
    return this.selectedTemplateType === "PLASTIC_NFC";
  }

  get activePrintSidesOptions(): PrintCardPrintSides[] {
    return this.printerSettings?.printSides ?? ["4/0", "4/4"];
  }

  printSidesLabelKey(side: PrintCardPrintSides): string {
    if (side === "4/0") {
      return "printCards.printerPrintSides40";
    }
    if (side === "4/4") {
      return "printCards.printerPrintSides44";
    }
    if (side === "5/0") {
      return "printCards.printerPrintSides50";
    }
    return "printCards.printerPrintSides55";
  }

  get frontBackgroundImageUrl(): string {
    return this.document.front.backgroundImageUrl?.trim() || "";
  }

  get backBackgroundImageUrl(): string {
    return this.document.back.backgroundImageUrl?.trim() || "";
  }

  get isLayeredLayout(): boolean {
    const layout = this.selectedLayout;
    if (!layout) {
      return false;
    }
    return (
      layout.fronts.some((variant) => variant.key !== "default") ||
      layout.backs.some((variant) => variant.key !== "default")
    );
  }

  get selectedLayout(): PrintCardLayoutCatalogItem | null {
    if (!this.selectedLayoutKey) {
      return null;
    }
    return (
      this.layouts.find((item) => item.key === this.selectedLayoutKey) ?? null
    );
  }

  get availableQuantityOptions(): number[] {
    if (this.selectedTemplateType === "PLASTIC_NFC") {
      return this.plasticQuantityOptions;
    }
    if (this.selectedTemplateType === "PAPER_NFC") {
      return [...PRINT_CARD_MULTILOFT_QUANTITY_OPTIONS];
    }
    return [...PRINT_CARD_PAPER_QUANTITY_OPTIONS];
  }

  get canAddPlasticQuantityOption(): boolean {
    if (
      this.plasticQuantityOptions.length >= PRINT_CARD_ALLOWED_QUANTITIES_MAX
    ) {
      return false;
    }
    const quantity = Math.floor(Number(this.plasticQuantityDraft));
    if (
      !Number.isFinite(quantity) ||
      quantity < PRINT_CARD_PLASTIC_QUANTITY_MIN ||
      quantity > PRINT_CARD_PLASTIC_QUANTITY_MAX
    ) {
      return false;
    }
    return !this.plasticQuantityOptions.includes(quantity);
  }

  addPlasticQuantityOption(): void {
    if (!this.canAddPlasticQuantityOption) {
      return;
    }
    const quantity = Math.floor(Number(this.plasticQuantityDraft));
    this.plasticQuantityOptions = [
      ...new Set([...this.plasticQuantityOptions, quantity]),
    ].sort((a, b) => a - b);
    const selected = [
      ...new Set([...this.form.controls.allowedQuantities.value, quantity]),
    ]
      .filter((value) => value !== this.plasticQuantityAddSentinel)
      .sort((a, b) => a - b);
    this.form.patchValue({ allowedQuantities: selected });
    this.form.controls.allowedQuantities.markAsDirty();
    this.plasticQuantityDraft = null;
  }

  stripPlasticQuantityAddSentinel(): void {
    if (!this.isPlasticNfcTemplate) {
      return;
    }
    const current = this.form.controls.allowedQuantities.value;
    if (!current.includes(this.plasticQuantityAddSentinel)) {
      return;
    }
    this.form.patchValue(
      {
        allowedQuantities: current.filter(
          (value) => value !== this.plasticQuantityAddSentinel,
        ),
      },
      { emitEvent: false },
    );
  }

  get availableBrandSides(): PrintCardLayoutBrandSide[] {
    const layout = this.selectedLayout;
    if (!layout) {
      return [];
    }
    const sides = new Set<PrintCardLayoutBrandSide>();
    for (const front of layout.fronts) {
      const side = front.meta?.brandSide;
      if (side === "left" || side === "right" || side === "stack-left") {
        sides.add(side);
      }
    }
    return FRONT_BRAND_SIDE_ORDER.filter((side) => sides.has(side));
  }

  get availableFrontMarkers(): PrintCardLayoutMarker[] {
    const layout = this.selectedLayout;
    if (!layout) {
      return [];
    }
    const markers = new Set<PrintCardLayoutMarker>();
    for (const front of layout.fronts) {
      if (front.meta?.brandSide !== this.selectedBrandSide) {
        continue;
      }
      const marker = front.meta?.marker;
      if (marker === "none" || marker === "nfc" || marker === "qr") {
        markers.add(marker);
      }
    }
    return MARKER_ORDER.filter((marker) => markers.has(marker));
  }

  get availableBackStyles(): PrintCardLayoutBackStyle[] {
    const layout = this.selectedLayout;
    if (!layout) {
      return [];
    }
    const styles = new Set<PrintCardLayoutBackStyle>();
    for (const back of layout.backs) {
      const style = back.meta?.backStyle;
      if (style === "blank" || style === "tagline" || style === "brand") {
        styles.add(style);
      }
    }
    return BACK_STYLE_ORDER.filter((style) => styles.has(style));
  }

  get availableBackMarkers(): PrintCardLayoutMarker[] {
    const layout = this.selectedLayout;
    if (!layout) {
      return [];
    }
    const markers = new Set<PrintCardLayoutMarker>();
    for (const back of layout.backs) {
      if (back.meta?.backStyle !== this.selectedBackStyle) {
        continue;
      }
      const marker = back.meta?.marker;
      if (marker === "none" || marker === "nfc" || marker === "qr") {
        markers.add(marker);
      }
    }
    return MARKER_ORDER.filter((marker) => markers.has(marker));
  }

  get availablePageSizePresets(): PrintCardPageSizePreset[] {
    return printCardPageSizePresetsForTemplate(
      this.pageSizePresets,
      this.selectedTemplateType,
    );
  }

  get orderPageSizePresets(): PrintCardPageSizePreset[] {
    const filtered = printCardPageSizePresetsForTemplate(
      this.pageSizePresets,
      this.selectedTemplateType,
      this.form.controls.allowedPageSizePresetIds.value,
    );
    return filtered.length > 0 ? filtered : this.availablePageSizePresets;
  }

  get activeSideDesign(): PrintCardSideDesign {
    return this.activeSide === "front"
      ? this.document.front
      : this.document.back;
  }

  get selectedElement(): PrintCardElement | null {
    if (!this.selectedElementId) {
      return null;
    }
    return (
      this.activeSideDesign.elements.find(
        (el) => el.id === this.selectedElementId,
      ) ?? null
    );
  }

  get layerList(): PrintCardElement[] {
    return [...this.activeSideDesign.elements].sort(
      (a, b) => b.zIndex - a.zIndex,
    );
  }

  elementLayerLabel(el: PrintCardElement): string {
    return el.name?.trim() || printCardElementDefaultName(el);
  }

  get selectedFont(): {
    fontName: string;
    fontFamily: string;
    fontCssUrl: string | null;
  } {
    const font =
      this.fontPresets.find((item) => item.id === this.selectedFontId) ??
      this.fontPresets[0];
    if (font?.id === BRAND_FONT_PRESET_ID) {
      const family = this.companyFontFamily.trim();
      return {
        fontName:
          family ||
          (this.translate.instant("printCards.fontUploaded") as string),
        fontFamily: `"${family.replace(/"/g, "")}"`,
        fontCssUrl: null,
      };
    }
    return {
      fontName: font?.labelKey
        ? (this.translate.instant(font.labelKey) as string)
        : font?.label || "Roboto",
      fontFamily: font?.fontFamily ?? '"Roboto", Arial, sans-serif',
      fontCssUrl: font?.fontCssUrl ?? null,
    };
  }

  get canvasSampleTexts(): Partial<Record<string, string>> {
    const emp = this.previewEmployeeData;
    const picker = this.previewEmployee;
    const userName =
      emp?.fullName?.trim() ||
      [emp?.givenName, emp?.familyName].filter(Boolean).join(" ").trim() ||
      picker?.fullName?.trim() ||
      [picker?.givenName, picker?.familyName]
        .filter(Boolean)
        .join(" ")
        .trim() ||
      "";
    const phone = emp?.mobileNumber?.trim() || emp?.phoneNumber?.trim() || "";
    const email = emp?.email?.trim() || picker?.email?.trim() || "";
    const jobTitle =
      emp?.jobTitle?.trim() ||
      (typeof emp?.dynamicVariables?.["jobTitle"] === "string"
        ? emp.dynamicVariables["jobTitle"].trim()
        : "") ||
      "";
    const companyName =
      emp?.companyData?.name?.trim() || this.companyPreviewName || "";
    const companyAddress =
      emp?.companyData?.address?.trim() || this.companyPreviewAddress || "";
    const companyImageUrl =
      emp?.companyData?.photoUrl?.trim() || this.companyPreviewImageUrl || "";
    const companyWebsite = emp?.companyData?.website?.trim() || "";
    const companyPhone = emp?.companyData?.phone?.trim() || "";
    const next: Partial<Record<string, string>> = {
      ...(userName ? { userName } : {}),
      ...(jobTitle ? { jobTitle } : {}),
      ...(phone
        ? {
            phone,
            formattedPhoneNumber: phone,
            formattedMobileNumber: phone,
            rawPhoneNumber: phone,
            rawMobileNumber: phone,
          }
        : {}),
      ...(email ? { email, userEmail: email } : {}),
      ...(companyName ? { companyName } : {}),
      ...(companyAddress ? { companyAddress } : {}),
      ...(companyImageUrl ? { companyImageUrl } : {}),
      ...(companyWebsite ? { companyWebsite } : {}),
      ...(companyPhone ? { companyPhone } : {}),
      ...(emp?.givenName?.trim()
        ? { userGivenName: emp.givenName.trim() }
        : {}),
      ...(emp?.familyName?.trim()
        ? { userFamilyName: emp.familyName.trim() }
        : {}),
      ...(emp?.userAbout?.trim() ? { userAbout: emp.userAbout.trim() } : {}),
    };
    const dynamicVariables = emp?.dynamicVariables;
    if (dynamicVariables && typeof dynamicVariables === "object") {
      for (const [key, value] of Object.entries(dynamicVariables)) {
        if (typeof value === "string" && value.trim() && !next[key]) {
          next[key] = value.trim();
        }
      }
    }
    const prev = this.canvasSampleTextsCache;
    if (prev && JSON.stringify(prev) === JSON.stringify(next)) {
      return prev;
    }
    this.canvasSampleTextsCache = next;
    return next;
  }

  get squashTargetOptions(): Array<{ id: string; label: string }> {
    const selected = this.selectedElement;
    if (!selected || selected.type !== "text") {
      return [];
    }
    return this.activeSideDesign.elements
      .filter(
        (el): el is PrintCardTextElement =>
          el.type === "text" && el.id !== selected.id,
      )
      .map((el) => ({
        id: el.id,
        label: el.name?.trim() || printCardElementDefaultName(el),
      }));
  }

  get previewLoading(): boolean {
    return (
      this.loadingPreview ||
      !this.employeePickerReady ||
      this.previewEmployeeLoading
    );
  }

  get canvasLoading(): boolean {
    return !this.employeePickerReady || this.previewEmployeeLoading;
  }

  get canRequestPreview(): boolean {
    if (!this.pageReady) {
      return false;
    }
    if (!this.previewEmployee?.email?.trim()) {
      return false;
    }
    return this.layoutApplied;
  }

  queuePreview(): void {
    if (this.canRequestPreview && !this.previewFrontHtml) {
      this.loadingPreview = true;
    }
    this.previewTick$.next();
  }

  onEmployeePickerInitialResolved(): void {
    this.employeePickerReady = true;
    const email = this.previewEmployee?.email?.trim().toLowerCase() ?? "";
    this.previewEmployeeEmail$.next(email);
    if (this.showLayoutStart) {
      this.refreshLayoutThumbs();
    }
    if (!this.canRequestPreview) {
      this.loadingPreview = false;
      return;
    }
    if (!this.previewFrontHtml || this.loadingPreview) {
      this.queuePreview();
    }
  }

  private startEditor(): void {
    this.loadCatalogs(() => {
      if (this.printCardTemplateId) {
        this.loadTemplate(this.printCardTemplateId);
        return;
      }
      this.pageReady = true;
      this.loadingThumbs = true;
      this.loadingPreview = true;
      const first = this.layouts[0];
      if (first) {
        this.onLayoutSelect(first.key);
      }
      this.refreshLayoutThumbs();
    });
  }

  private loadCatalogs(onLayoutsReady?: () => void): void {
    this.printCardService
      .getPageSizePresets(this.selectedTemplateType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.pageSizePresets = res.presets ?? [];
        const defaultId = defaultPrintCardPageSizePresetId(
          this.selectedTemplateType,
        );
        let pageSizeId = this.form.controls.pageSizePreset.value;
        if (
          !this.availablePageSizePresets.some((item) => item.id === pageSizeId)
        ) {
          pageSizeId =
            this.availablePageSizePresets.find((item) => item.id === defaultId)
              ?.id ??
            this.availablePageSizePresets[0]?.id ??
            defaultId;
        }
        const allowedIds =
          this.form.controls.allowedPageSizePresetIds.value.length > 0
            ? this.form.controls.allowedPageSizePresetIds.value
            : this.availablePageSizePresets.map((item) => item.id);
        this.form.patchValue({
          pageSizePreset: pageSizeId,
          allowedPageSizePresetIds: allowedIds,
          allowedQuantities:
            this.form.controls.allowedQuantities.value.length > 0
              ? this.form.controls.allowedQuantities.value
              : this.availableQuantityOptions,
        });
        const preset =
          this.availablePageSizePresets.find(
            (item) => item.id === pageSizeId,
          ) ?? this.availablePageSizePresets[0];
        if (preset) {
          const page = printCardDesignPageFromPreset(
            preset,
            this.selectedOrientation,
          );
          if (this.showLayoutStart && !this.layoutApplied) {
            this.document = {
              ...this.document,
              orientation: this.selectedOrientation,
              page,
            };
          } else if (
            this.syncPageSizeAfterCatalogLoad &&
            !this.showLayoutStart &&
            this.layoutApplied &&
            (this.document.page.widthCm !== page.widthCm ||
              this.document.page.heightCm !== page.heightCm ||
              this.document.page.bleedMm !== page.bleedMm)
          ) {
            this.onPageSizeChange(pageSizeId);
          }
        }
        this.syncPageSizeAfterCatalogLoad = false;
      });

    this.printCardService
      .getPrinterSettings(this.selectedTemplateType)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null)),
      )
      .subscribe((settings) => {
        this.printerSettings = settings?.enabled ? settings : null;
        if (!this.printerSettings) {
          return;
        }
        const paperIds = new Set(
          this.printerSettings.papers.map((item) => item.id),
        );
        const spacerIds = new Set(
          (this.printerSettings.spacerColors ?? []).map((item) => item.id),
        );
        const currentPaper = this.form.controls.printerPaperId.value;
        const currentSides = this.form.controls.printSides.value;
        const currentSpacer = this.form.controls.printerSpacerColorId.value;
        const sides = this.printerSettings.printSides;
        this.form.patchValue({
          printerPaperId: paperIds.has(currentPaper)
            ? currentPaper
            : (this.printerSettings.defaults.paperId ??
              this.printerSettings.papers[0]?.id ??
              0),
          printSides: sides.includes(currentSides)
            ? currentSides
            : (this.printerSettings.defaults.printSides ?? sides[0] ?? "4/4"),
          printerSpacerColorId: spacerIds.has(currentSpacer)
            ? currentSpacer
            : (this.printerSettings.defaults.spacerColorId ??
              this.printerSettings.spacerColors?.[0]?.id ??
              0),
        });
      });

    this.printCardService
      .getLayoutCatalog(this.selectedTemplateType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.layouts = res.layouts ?? [];
        onLayoutsReady?.();
      });
  }

  private loadTemplate(printCardTemplateId: string): void {
    this.printCardService
      .getTemplate(printCardTemplateId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (template) => {
          this.pendingFontSettings = {
            fontName: template.settings?.fontName,
            fontFamily: template.settings?.fontFamily,
            fontCssUrl: template.settings?.fontCssUrl,
          };
          this.applyTemplateFontPreset();
          const frontBg = template.settings?.frontBackgroundColor?.trim() ?? "";
          this.frontBackgroundColor = HEX_COLOR.test(frontBg)
            ? frontBg.toUpperCase()
            : PRINT_CARD_DEFAULT_SIDE_BACKGROUND;
          const backBg = template.settings?.backBackgroundColor?.trim() ?? "";
          this.backBackgroundColor = HEX_COLOR.test(backBg)
            ? backBg.toUpperCase()
            : PRINT_CARD_DEFAULT_SIDE_BACKGROUND;
          const frontText = template.settings?.frontTextColor?.trim() ?? "";
          this.frontTextColor = HEX_COLOR.test(frontText)
            ? frontText.toUpperCase()
            : PRINT_CARD_DEFAULT_SIDE_TEXT;
          const backText = template.settings?.backTextColor?.trim() ?? "";
          this.backTextColor = HEX_COLOR.test(backText)
            ? backText.toUpperCase()
            : PRINT_CARD_DEFAULT_SIDE_TEXT;
          const frontNfc = template.settings?.frontNfcColor?.trim() ?? "";
          this.frontNfcColor = HEX_COLOR.test(frontNfc)
            ? frontNfc.toUpperCase()
            : PRINT_CARD_DEFAULT_NFC_COLOR;
          const backNfc = template.settings?.backNfcColor?.trim() ?? "";
          this.backNfcColor = HEX_COLOR.test(backNfc)
            ? backNfc.toUpperCase()
            : PRINT_CARD_DEFAULT_NFC_COLOR;
          const frontBgImage =
            typeof template.settings?.frontBackgroundImageUrl === "string"
              ? template.settings.frontBackgroundImageUrl.trim()
              : "";
          const backBgImage =
            typeof template.settings?.backBackgroundImageUrl === "string"
              ? template.settings.backBackgroundImageUrl.trim()
              : "";
          if (frontBgImage) {
            this.document = {
              ...this.document,
              front: {
                ...this.document.front,
                backgroundImageUrl: frontBgImage,
              },
            };
          }
          if (backBgImage) {
            this.document = {
              ...this.document,
              back: {
                ...this.document.back,
                backgroundImageUrl: backBgImage,
              },
            };
          }
          this.form.patchValue({
            name: template.name,
            templateType: template.type,
            digitalCardProfileId: template.digitalCardProfileId?.trim() || null,
            allowedPageSizePresetIds: template.settings
              ?.allowedPageSizePresetIds?.length
              ? [...template.settings.allowedPageSizePresetIds]
              : [],
            allowedQuantities: (() => {
              if (template.type === "PLASTIC_NFC") {
                const plasticQuantities = [
                  ...new Set(
                    (template.settings?.allowedQuantities ?? [])
                      .map((quantity) => Math.floor(Number(quantity)))
                      .filter(
                        (quantity) =>
                          Number.isInteger(quantity) &&
                          quantity >= PRINT_CARD_PLASTIC_QUANTITY_MIN &&
                          quantity <= PRINT_CARD_PLASTIC_QUANTITY_MAX,
                      ),
                  ),
                ]
                  .sort((a, b) => a - b)
                  .slice(0, PRINT_CARD_ALLOWED_QUANTITIES_MAX);
                this.plasticQuantityOptions = [...plasticQuantities];
                this.plasticQuantityDraft = null;
                return plasticQuantities;
              }
              this.plasticQuantityOptions = [];
              this.plasticQuantityDraft = null;
              return template.settings?.allowedQuantities?.length
                ? [...template.settings.allowedQuantities]
                : [...this.availableQuantityOptions];
            })(),
            ...(typeof template.settings?.printerPaperId === "number"
              ? { printerPaperId: template.settings.printerPaperId }
              : {}),
            ...(template.settings?.printSides
              ? { printSides: template.settings.printSides }
              : {}),
            ...(typeof template.settings?.printerSpacerColorId === "number"
              ? { printerSpacerColorId: template.settings.printerSpacerColorId }
              : {}),
          });
          this.loadCatalogs(() => {
            const existing = template.settings?.designDocument;
            if (
              existing?.designVersion === 3 ||
              existing?.designVersion === 4
            ) {
              const orientation =
                existing.orientation === "portrait" ||
                template.settings?.orientation === "portrait"
                  ? "portrait"
                  : "landscape";
              this.form.patchValue({ orientation });
              const matched =
                this.orderPageSizePresets.find(
                  (item) =>
                    item.id === existing.activePageSizePresetId ||
                    (item.widthCm === existing.page.widthCm &&
                      item.heightCm === existing.page.heightCm) ||
                    (item.widthCm === existing.page.heightCm &&
                      item.heightCm === existing.page.widthCm),
                ) ?? this.orderPageSizePresets[0];
              const migrated = migratePrintCardDesignDocument(
                { ...existing, orientation },
                matched?.id ?? null,
              );
              this.replaceDocument(
                ensurePrintCardSizeLayouts(
                  migrated,
                  this.orderPageSizePresets,
                  matched?.id ?? null,
                ),
              );
              if (
                frontBgImage &&
                !this.document.front.backgroundImageUrl?.trim()
              ) {
                this.document = {
                  ...this.document,
                  front: {
                    ...this.document.front,
                    backgroundImageUrl: frontBgImage,
                  },
                };
              }
              if (
                backBgImage &&
                !this.document.back.backgroundImageUrl?.trim()
              ) {
                this.document = {
                  ...this.document,
                  back: {
                    ...this.document.back,
                    backgroundImageUrl: backBgImage,
                  },
                };
              }
              this.document = syncPrintCardActiveSizeLayout(
                this.document,
                matched?.id ?? this.selectedPageSizePresetId,
              );
              this.frontBackgroundColor = this.document.front.backgroundColor;
              this.backBackgroundColor = this.document.back.backgroundColor;
              this.layoutApplied = true;
              this.selectedLayoutKey = existing.layoutKey ?? null;
              this.syncVariantSelectionFromDocument();
              this.setupFrontHtml = "";
              this.setupBackHtml = "";
              if (matched) {
                this.form.patchValue({ pageSizePreset: matched.id });
              }
              this.showLayoutStart = false;
              this.pageReady = true;
              this.queuePreview();
              return;
            }
            const orientation =
              template.settings?.orientation === "portrait"
                ? "portrait"
                : "landscape";
            this.form.patchValue({ orientation });
            this.setupFrontHtml = template.frontContent ?? "";
            this.setupBackHtml = template.backContent ?? "";
            if (this.setupFrontHtml.trim()) {
              this.writeSideColorsToSetupHtml("front");
              this.writeSideColorsToSetupHtml("back");
              this.syncDocumentFromSetupHtml();
              this.layoutApplied = true;
              this.selectedLayoutKey =
                template.settings?.layoutKey ?? this.selectedLayoutKey;
              this.syncVariantSelectionFromDocument();
            } else {
              this.document = {
                ...this.document,
                orientation,
              };
            }
            this.showLayoutStart = false;
            this.pageReady = true;
            this.queuePreview();
          });
        },
        error: () => {
          this.toastr.error(
            this.translate.instant("printCards.templateLoadFailed") as string,
          );
          void this.router.navigate(["/print-cards"]);
        },
      });
  }

  onTemplateTypeChange(type: PrintCardTemplateType): void {
    this.plasticQuantityDraft = null;
    if (type === "PLASTIC_NFC") {
      this.plasticQuantityOptions = [];
      this.form.patchValue({
        templateType: type,
        allowedPageSizePresetIds: [],
        allowedQuantities: [],
      });
    } else {
      this.plasticQuantityOptions = [];
      this.form.patchValue({
        templateType: type,
        allowedPageSizePresetIds: [],
      });
      this.form.patchValue({
        allowedQuantities: [...this.availableQuantityOptions],
      });
    }
    if (this.showLayoutStart) {
      this.selectedLayoutKey = null;
      this.layoutApplied = false;
    } else {
      this.syncPageSizeAfterCatalogLoad = true;
    }
    this.loadCatalogs(() => {
      this.refreshLayoutThumbs();
      if (this.showLayoutStart) {
        const first = this.layouts[0];
        if (first) {
          this.onLayoutSelect(first.key);
        }
      }
      this.queuePreview();
    });
  }

  onPageSizeChange(presetId: string): void {
    const preset = this.orderPageSizePresets.find(
      (item) => item.id === presetId,
    );
    if (!preset || presetId === this.document.activePageSizePresetId) {
      if (preset) {
        this.form.patchValue({ pageSizePreset: presetId });
      }
      return;
    }
    this.pushHistory();
    const synced = syncPrintCardActiveSizeLayout(
      this.document,
      this.document.activePageSizePresetId ?? this.selectedPageSizePresetId,
    );
    const withLayouts = ensurePrintCardSizeLayouts(
      synced,
      this.orderPageSizePresets,
      synced.activePageSizePresetId,
    );
    const merged = mergePrintCardSizeLayout(withLayouts, presetId);
    this.document = merged;
    this.frontBackgroundColor = merged.front.backgroundColor;
    this.backBackgroundColor = merged.back.backgroundColor;
    this.form.patchValue({ pageSizePreset: presetId });
    const allowed = this.form.controls.allowedPageSizePresetIds.value;
    if (!allowed.includes(presetId)) {
      this.form.patchValue({
        allowedPageSizePresetIds: [...allowed, presetId],
      });
    }
    this.redoStack = [];
    if (this.showLayoutStart && this.layoutApplied) {
      this.syncDocumentFromSetupHtml();
    }
    this.queuePreview();
    if (this.showLayoutStart) {
      this.refreshLayoutThumbs();
    }
  }

  onOrientationChange(orientation: PrintCardOrientation): void {
    const next = orientation === "portrait" ? "portrait" : "landscape";
    if (next === (this.document.orientation ?? "landscape")) {
      this.form.patchValue({ orientation: next });
      return;
    }
    this.pushHistory();
    this.form.patchValue({ orientation: next });
    const preset =
      this.orderPageSizePresets.find(
        (item) => item.id === this.selectedPageSizePresetId,
      ) ?? this.orderPageSizePresets[0];
    let nextDocument: PrintCardDesignDocument = {
      ...this.document,
      orientation: next,
      page: preset
        ? printCardDesignPageFromPreset(preset, next)
        : {
            ...this.document.page,
            widthCm: this.document.page.heightCm,
            heightCm: this.document.page.widthCm,
          },
    };
    if (this.orderPageSizePresets.length > 0) {
      nextDocument = ensurePrintCardSizeLayouts(
        syncPrintCardActiveSizeLayout(
          nextDocument,
          nextDocument.activePageSizePresetId ?? this.selectedPageSizePresetId,
        ),
        this.orderPageSizePresets,
        this.selectedPageSizePresetId,
      );
    }
    this.document = nextDocument;
    this.frontBackgroundColor = this.document.front.backgroundColor;
    this.backBackgroundColor = this.document.back.backgroundColor;
    this.redoStack = [];
    if (this.showLayoutStart && this.layoutApplied) {
      this.applySelectedLayoutVariants();
    } else {
      this.queuePreview();
    }
    if (this.showLayoutStart) {
      this.refreshLayoutThumbs();
    }
  }

  onResetSizePositions(): void {
    if (this.orderPageSizePresets.length === 0) {
      return;
    }
    this.pushHistory();
    const activeId =
      this.document.activePageSizePresetId ?? this.selectedPageSizePresetId;
    const next = resetPrintCardActiveSizeLayoutPositions(
      this.document,
      this.orderPageSizePresets,
      activeId,
    );
    this.document = next;
    this.frontBackgroundColor = next.front.backgroundColor;
    this.backBackgroundColor = next.back.backgroundColor;
    this.redoStack = [];
    this.queuePreview();
  }

  private syncAllowedSizeLayouts(): void {
    if (!this.layoutApplied && !this.document.front.elements.length) {
      return;
    }
    const presets = this.orderPageSizePresets;
    if (presets.length === 0) {
      return;
    }
    const activeId =
      this.document.activePageSizePresetId ?? this.selectedPageSizePresetId;
    this.document = ensurePrintCardSizeLayouts(
      this.document,
      presets,
      activeId,
    );
    const nextActive = this.document.activePageSizePresetId ?? presets[0]?.id;
    if (nextActive && nextActive !== this.selectedPageSizePresetId) {
      this.form.patchValue(
        { pageSizePreset: nextActive },
        { emitEvent: false },
      );
    }
    this.frontBackgroundColor = this.document.front.backgroundColor;
    this.backBackgroundColor = this.document.back.backgroundColor;
    this.queuePreview();
  }

  onFontChange(fontId: string): void {
    this.form.patchValue({ fontPreset: fontId });
    this.queuePreview();
    if (this.showLayoutStart) {
      this.refreshLayoutThumbs();
    }
  }

  onDigitalCardProfileIdChange(value: string | null): void {
    const next = value?.trim() || null;
    if (
      (this.form.controls.digitalCardProfileId.value?.trim() || null) !== next
    ) {
      this.form.patchValue({ digitalCardProfileId: next });
    }
    this.queuePreview();
    if (this.showLayoutStart) {
      this.refreshLayoutThumbs();
    }
  }

  onPreviewEmployeeChange(emp: EmployeePickerItem | null): void {
    const nextId = emp?.employeeId?.trim() || "";
    const nextEmail = emp?.email?.trim() || "";
    const prevId = this.previewEmployee?.employeeId?.trim() || "";
    const prevEmail = this.previewEmployee?.email?.trim() || "";
    this.previewEmployee = emp;
    this.previewEmployeeEmail$.next(nextEmail.toLowerCase());
    if (nextId === prevId && nextEmail === prevEmail) {
      return;
    }
    this.queuePreview();
    if (this.showLayoutStart) {
      this.refreshLayoutThumbs();
    }
  }

  onLayoutSelect(layoutKey: string): void {
    const layout = this.layouts.find((item) => item.key === layoutKey);
    if (!layout) {
      return;
    }
    this.selectedLayoutKey = layout.key;
    const brandSides = this.availableBrandSides;
    this.selectedBrandSide = brandSides[0] ?? "right";
    const frontMarkers = this.availableFrontMarkers;
    this.selectedFrontMarker = frontMarkers[0] ?? "none";
    const backStyles = this.availableBackStyles;
    this.selectedBackStyle = backStyles[0] ?? "blank";
    const backMarkers = this.availableBackMarkers;
    this.selectedBackMarker = backMarkers[0] ?? "none";
    this.applySelectedLayoutVariants();
  }

  onBrandSideSelect(side: PrintCardLayoutBrandSide): void {
    this.selectedBrandSide = side;
    if (!this.availableFrontMarkers.includes(this.selectedFrontMarker)) {
      this.selectedFrontMarker = this.availableFrontMarkers[0] ?? "none";
    }
    this.applySelectedLayoutVariants();
  }

  onFrontMarkerSelect(marker: PrintCardLayoutMarker): void {
    this.selectedFrontMarker = marker;
    this.applySelectedLayoutVariants();
  }

  onBackStyleSelect(style: PrintCardLayoutBackStyle): void {
    this.selectedBackStyle = style;
    if (!this.availableBackMarkers.includes(this.selectedBackMarker)) {
      this.selectedBackMarker = this.availableBackMarkers[0] ?? "none";
    }
    this.applySelectedLayoutVariants();
  }

  onBackMarkerSelect(marker: PrintCardLayoutMarker): void {
    this.selectedBackMarker = marker;
    this.applySelectedLayoutVariants();
  }

  enterCanvas(): void {
    if (!this.layoutApplied) {
      return;
    }
    this.showLayoutStart = false;
    this.queuePreview();
  }

  onFrontTextColorChange(value: string): void {
    const next = this.hexColor(value, PRINT_CARD_DEFAULT_SIDE_TEXT);
    if (next === this.frontTextColor) {
      return;
    }
    this.frontTextColor = next;
    this.writeSideColorsToSetupHtml("front");
    this.applyColorsToDocumentElements();
    this.queuePreview();
    if (this.showLayoutStart) {
      this.refreshLayoutThumbs();
    }
  }

  onBackTextColorChange(value: string): void {
    const next = this.hexColor(value, PRINT_CARD_DEFAULT_SIDE_TEXT);
    if (next === this.backTextColor) {
      return;
    }
    this.backTextColor = next;
    this.writeSideColorsToSetupHtml("back");
    this.applyColorsToDocumentElements();
    this.queuePreview();
    if (this.showLayoutStart) {
      this.refreshLayoutThumbs();
    }
  }

  onFrontBackgroundColorChange(value: string): void {
    const next = this.hexColor(value, PRINT_CARD_DEFAULT_SIDE_BACKGROUND);
    if (next === this.frontBackgroundColor) {
      return;
    }
    this.frontBackgroundColor = next;
    this.writeSideColorsToSetupHtml("front");
    this.applyColorsToDocumentElements();
    this.queuePreview();
    if (this.showLayoutStart) {
      this.refreshLayoutThumbs();
    }
  }

  onBackBackgroundColorChange(value: string): void {
    const next = this.hexColor(value, PRINT_CARD_DEFAULT_SIDE_BACKGROUND);
    if (next === this.backBackgroundColor) {
      return;
    }
    this.backBackgroundColor = next;
    this.writeSideColorsToSetupHtml("back");
    this.applyColorsToDocumentElements();
    this.queuePreview();
    if (this.showLayoutStart) {
      this.refreshLayoutThumbs();
    }
  }

  onFrontNfcColorChange(value: string): void {
    const next = this.hexColor(value, PRINT_CARD_DEFAULT_NFC_COLOR);
    if (next === this.frontNfcColor) {
      return;
    }
    this.frontNfcColor = next;
    this.applyColorsToDocumentElements();
    this.queuePreview();
    if (this.showLayoutStart) {
      this.refreshLayoutThumbs();
    }
  }

  onBackNfcColorChange(value: string): void {
    const next = this.hexColor(value, PRINT_CARD_DEFAULT_NFC_COLOR);
    if (next === this.backNfcColor) {
      return;
    }
    this.backNfcColor = next;
    this.applyColorsToDocumentElements();
    this.queuePreview();
    if (this.showLayoutStart) {
      this.refreshLayoutThumbs();
    }
  }

  private hexColor(value: string, fallback: string): string {
    const trimmed = value.trim();
    return HEX_COLOR.test(trimmed) ? trimmed.toUpperCase() : fallback;
  }

  private findFrontVariant(): PrintCardLayoutVariant | null {
    const layout = this.selectedLayout;
    if (!layout) {
      return null;
    }
    const frontKey =
      this.selectedFrontMarker === "none"
        ? this.selectedBrandSide
        : `${this.selectedBrandSide}-${this.selectedFrontMarker}`;
    return (
      layout.fronts.find((item) => item.key === frontKey) ??
      layout.fronts.find(
        (item) =>
          item.meta?.brandSide === this.selectedBrandSide &&
          item.meta?.marker === this.selectedFrontMarker,
      ) ??
      layout.fronts[0] ??
      null
    );
  }

  private findBackVariant(): PrintCardLayoutVariant | null {
    const layout = this.selectedLayout;
    if (!layout) {
      return null;
    }
    const backKey =
      this.selectedBackMarker === "none"
        ? this.selectedBackStyle
        : `${this.selectedBackStyle}-${this.selectedBackMarker}`;
    return (
      layout.backs.find((item) => item.key === backKey) ??
      layout.backs.find(
        (item) =>
          item.meta?.backStyle === this.selectedBackStyle &&
          item.meta?.marker === this.selectedBackMarker,
      ) ??
      layout.backs[0] ??
      null
    );
  }

  private applySelectedLayoutVariants(): void {
    const layout = this.selectedLayout;
    const front = this.findFrontVariant();
    const back = this.findBackVariant();
    if (!layout || !front || !back) {
      return;
    }
    if (
      front.meta?.brandSide === "left" ||
      front.meta?.brandSide === "right" ||
      front.meta?.brandSide === "stack-left"
    ) {
      this.selectedBrandSide = front.meta.brandSide;
    }
    if (
      front.meta?.marker === "none" ||
      front.meta?.marker === "nfc" ||
      front.meta?.marker === "qr"
    ) {
      this.selectedFrontMarker = front.meta.marker;
    }
    if (
      back.meta?.backStyle === "blank" ||
      back.meta?.backStyle === "tagline" ||
      back.meta?.backStyle === "brand"
    ) {
      this.selectedBackStyle = back.meta.backStyle;
    }
    if (
      back.meta?.marker === "none" ||
      back.meta?.marker === "nfc" ||
      back.meta?.marker === "qr"
    ) {
      this.selectedBackMarker = back.meta.marker;
    }
    this.setupFrontHtml = this.layoutVariantContent(front);
    this.setupBackHtml = this.layoutVariantContent(back);
    this.writeSideColorsToSetupHtml("front");
    this.writeSideColorsToSetupHtml("back");
    this.syncPrintSidesForBackVariant(back);
    this.syncDocumentFromSetupHtml();
    this.layoutApplied = true;
    this.queuePreview();
  }

  private layoutVariantContent(
    variant: PrintCardLayoutVariant,
    orientation: PrintCardOrientation = this.selectedOrientation,
  ): string {
    if (orientation === "portrait") {
      const portrait = variant.contentPortrait?.trim();
      if (portrait) {
        return portrait;
      }
    }
    return variant.content;
  }

  private buildCompiledLayoutVariants(
    page: {
      widthCm: number;
      heightCm: number;
      bleedMm: number;
      safeZoneInsetMm: number;
    },
    orientation: PrintCardOrientation,
  ): Record<string, string> {
    const emptyFront =
      '<div class="container"><div class="pc-front"></div></div>';
    const emptyBack =
      '<div class="container"><div class="pc-back"></div></div>';
    const compiled: Record<string, string> = {};
    for (const layout of this.layouts) {
      for (const variant of layout.fronts) {
        const key = `${layout.key}:front:${variant.key}`;
        const doc = printCardDesignFromLayoutHtml({
          frontContent: this.layoutVariantContent(variant, orientation),
          backContent: emptyBack,
          page,
          orientation,
          pageSizePresetId: this.selectedPageSizePresetId,
          frontBackgroundColor: this.frontBackgroundColor,
          backBackgroundColor: this.backBackgroundColor,
        });
        compiled[key] = compilePrintCardDesign(doc).frontContent;
      }
      for (const variant of layout.backs) {
        const key = `${layout.key}:back:${variant.key}`;
        const doc = printCardDesignFromLayoutHtml({
          frontContent: emptyFront,
          backContent: this.layoutVariantContent(variant, orientation),
          page,
          orientation,
          pageSizePresetId: this.selectedPageSizePresetId,
          frontBackgroundColor: this.frontBackgroundColor,
          backBackgroundColor: this.backBackgroundColor,
        });
        compiled[key] = compilePrintCardDesign(doc).backContent;
      }
    }
    return compiled;
  }

  private syncDocumentFromSetupHtml(): void {
    const layout = this.selectedLayout;
    const front = this.findFrontVariant();
    const back = this.findBackVariant();
    const preset =
      this.orderPageSizePresets.find(
        (item) => item.id === this.selectedPageSizePresetId,
      ) ?? this.orderPageSizePresets[0];
    const frontContent = this.setupFrontHtml.trim();
    const backContent = this.setupBackHtml.trim();
    if (!frontContent || !preset) {
      return;
    }
    const orientation = this.selectedOrientation;
    this.replaceDocument(
      printCardDesignFromLayoutHtml({
        frontContent,
        backContent,
        page: printCardDesignPageFromPreset(preset, orientation),
        pageSizePresetId: preset.id,
        orientation,
        layoutKey: layout?.key ?? this.selectedLayoutKey,
        layoutFrontVariantKey: front?.key ?? null,
        layoutBackVariantKey: back?.key ?? null,
        frontBackgroundColor: this.frontBackgroundColor,
        backBackgroundColor: this.backBackgroundColor,
        frontBackgroundImageUrl: this.document.front.backgroundImageUrl ?? null,
        backBackgroundImageUrl: this.document.back.backgroundImageUrl ?? null,
      }),
    );
    this.document = ensurePrintCardSizeLayouts(
      this.document,
      this.orderPageSizePresets,
      preset.id,
    );
    this.applyColorsToDocumentElements();
  }

  private applyColorsToDocumentElements(): void {
    const next = this.clone(this.document);
    for (const el of next.front.elements) {
      if (el.type === "text") {
        el.color = this.frontTextColor;
      }
      if (el.type === "nfc") {
        el.color = this.frontNfcColor;
      }
    }
    for (const el of next.back.elements) {
      if (el.type === "text") {
        el.color = this.backTextColor;
      }
      if (el.type === "nfc") {
        el.color = this.backNfcColor;
      }
    }
    next.front.backgroundColor = this.frontBackgroundColor;
    next.back.backgroundColor = this.backBackgroundColor;
    this.document = syncPrintCardActiveSizeLayout(next);
  }

  private writeSideColorsToSetupHtml(side: "front" | "back"): void {
    const bg =
      side === "front" ? this.frontBackgroundColor : this.backBackgroundColor;
    const text = side === "front" ? this.frontTextColor : this.backTextColor;
    const bgHex = HEX_COLOR.test(bg)
      ? bg.toUpperCase()
      : PRINT_CARD_DEFAULT_SIDE_BACKGROUND;
    const textHex = HEX_COLOR.test(text)
      ? text.toUpperCase()
      : PRINT_CARD_DEFAULT_SIDE_TEXT;
    const scopeRule = `.pc-font-scope.pc-side-${side} {\n  --pc-side-bg: ${bgHex};\n  --pc-side-text: ${textHex};\n}`;
    const scopeRe = new RegExp(
      `\\.pc-font-scope\\.pc-side-${side}\\s*\\{[^}]*\\}`,
      "i",
    );
    let content = side === "front" ? this.setupFrontHtml : this.setupBackHtml;
    const styleMatch = content.match(/^<style\b[^>]*>([\s\S]*?)<\/style>/i);
    if (styleMatch) {
      let css = styleMatch[1].replace(scopeRe, "").trim();
      css = `${scopeRule}\n${css}`.trim();
      content = `<style>\n${css}\n</style>${content.slice(styleMatch[0].length)}`;
    } else {
      content = `<style>\n${scopeRule}\n</style>\n${content}`.trim();
    }
    if (side === "front") {
      this.setupFrontHtml = content;
    } else {
      this.setupBackHtml = content;
    }
  }

  private syncPrintSidesForBackVariant(back: PrintCardLayoutVariant): void {
    if (!this.hasPrinterOptions) {
      return;
    }
    const sides = this.activePrintSidesOptions;
    const isBlankNone =
      back.meta?.backStyle === "blank" && back.meta?.marker === "none";
    const preferred: PrintCardPrintSides = isBlankNone ? "4/0" : "4/4";
    if (sides.includes(preferred)) {
      this.form.patchValue({ printSides: preferred });
    }
  }

  private syncVariantSelectionFromDocument(): void {
    const layoutKey = this.document.layoutKey;
    if (layoutKey) {
      this.selectedLayoutKey = layoutKey;
    }
    const layout = this.selectedLayout;
    const frontKey = this.document.layoutFrontVariantKey;
    const backKey = this.document.layoutBackVariantKey;
    const front =
      layout?.fronts.find((item) => item.key === frontKey) ?? layout?.fronts[0];
    const back =
      layout?.backs.find((item) => item.key === backKey) ?? layout?.backs[0];
    if (
      front?.meta?.brandSide === "left" ||
      front?.meta?.brandSide === "right" ||
      front?.meta?.brandSide === "stack-left"
    ) {
      this.selectedBrandSide = front.meta.brandSide;
    }
    if (
      front?.meta?.marker === "none" ||
      front?.meta?.marker === "nfc" ||
      front?.meta?.marker === "qr"
    ) {
      this.selectedFrontMarker = front.meta.marker;
    }
    if (
      back?.meta?.backStyle === "blank" ||
      back?.meta?.backStyle === "tagline" ||
      back?.meta?.backStyle === "brand"
    ) {
      this.selectedBackStyle = back.meta.backStyle;
    }
    if (
      back?.meta?.marker === "none" ||
      back?.meta?.marker === "nfc" ||
      back?.meta?.marker === "qr"
    ) {
      this.selectedBackMarker = back.meta.marker;
    }
    this.frontBackgroundColor =
      this.document.front.backgroundColor || PRINT_CARD_DEFAULT_SIDE_BACKGROUND;
    this.backBackgroundColor =
      this.document.back.backgroundColor || PRINT_CARD_DEFAULT_SIDE_BACKGROUND;
    const frontText = this.document.front.elements.find(
      (el): el is Extract<PrintCardElement, { type: "text" }> =>
        el.type === "text" && !!el.color,
    );
    const backText = this.document.back.elements.find(
      (el): el is Extract<PrintCardElement, { type: "text" }> =>
        el.type === "text" && !!el.color,
    );
    if (frontText?.color && HEX_COLOR.test(frontText.color)) {
      this.frontTextColor = frontText.color.toUpperCase();
    }
    if (backText?.color && HEX_COLOR.test(backText.color)) {
      this.backTextColor = backText.color.toUpperCase();
    }
    const frontNfc = this.document.front.elements.find(
      (el): el is Extract<PrintCardElement, { type: "nfc" }> =>
        el.type === "nfc",
    );
    const backNfc = this.document.back.elements.find(
      (el): el is Extract<PrintCardElement, { type: "nfc" }> =>
        el.type === "nfc",
    );
    if (frontNfc?.color && HEX_COLOR.test(frontNfc.color)) {
      this.frontNfcColor = frontNfc.color.toUpperCase();
    }
    if (backNfc?.color && HEX_COLOR.test(backNfc.color)) {
      this.backNfcColor = backNfc.color.toUpperCase();
    }
  }

  readonly mediaFilePreview = mediaFilePreview;

  backgroundImageLabel(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) {
      return "";
    }
    const found = this.mediaImages.find((image) => image.imageUrl === trimmed);
    return found?.name?.trim() || trimmed;
  }

  orphanBackgroundImageUrl(side: "front" | "back"): string | null {
    const trimmed =
      side === "front"
        ? this.frontBackgroundImageUrl
        : this.backBackgroundImageUrl;
    if (!trimmed) {
      return null;
    }
    return this.mediaImages.some((image) => image.imageUrl === trimmed)
      ? null
      : trimmed;
  }

  setSideBackgroundImageUrl(side: "front" | "back", value: string): void {
    const trimmed = typeof value === "string" ? value.trim() : "";
    this.pushHistory();
    const next = this.clone(this.document);
    if (side === "front") {
      next.front.backgroundImageUrl = trimmed || null;
    } else {
      next.back.backgroundImageUrl = trimmed || null;
    }
    this.document = syncPrintCardActiveSizeLayout(next);
    this.redoStack = [];
    this.queuePreview();
    if (this.showLayoutStart) {
      this.refreshLayoutThumbs();
    }
  }

  private loadMediaImages(): void {
    this.mediaImagesLoading = true;
    this.imagesService
      .getImages()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list) => {
          this.mediaImages = Array.isArray(list) ? list : [];
          this.mediaImagesLoading = false;
        },
        error: () => {
          this.mediaImages = [];
          this.mediaImagesLoading = false;
        },
      });
  }

  changeLayout(): void {
    const hasDesign =
      this.document.front.elements.length > 0 ||
      this.document.back.elements.length > 0;
    if (!hasDesign) {
      this.openLayoutBrowser();
      return;
    }
    const data: ConfirmationDialogParams = {
      title: "printCards.changeLayout",
      body: "printCards.changeLayoutConfirm",
      confirmButton: "printCards.changeLayout",
    };
    this.dialog
      .open(ConfirmationDialogComponent, {
        width: "400px",
        data,
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed: boolean | undefined) => {
        if (!confirmed) {
          return;
        }
        this.openLayoutBrowser();
      });
  }

  private openLayoutBrowser(): void {
    this.showLayoutStart = true;
    if (this.selectedLayout) {
      this.applySelectedLayoutVariants();
    } else {
      const first = this.layouts[0];
      if (first) {
        this.onLayoutSelect(first.key);
      } else {
        this.layoutApplied = false;
        this.setupFrontHtml = "";
        this.setupBackHtml = "";
      }
    }
    this.refreshLayoutThumbs();
  }

  layoutThumbHtml(
    layoutKey: string,
    side: "front" | "back",
    variantKey: string,
  ): string | null {
    const html = this.layoutThumbs[`${layoutKey}:${side}:${variantKey}`];
    return html?.trim() ? html : null;
  }

  private refreshLayoutThumbs(): void {
    if (!this.showLayoutStart || !this.layouts.length) {
      this.loadingThumbs = false;
      return;
    }
    this.loadingThumbs = true;
    if (!this.previewEmployee?.email?.trim()) {
      return;
    }
    this.thumbsTick$.next();
  }

  replaceDocument(doc: PrintCardDesignDocument): void {
    this.undoStack = [];
    this.redoStack = [];
    const named = this.clone(doc);
    named.front.elements = named.front.elements.map((el) => {
      const name = el.name?.trim() || printCardElementDefaultName(el);
      return name === el.name ? el : { ...el, name };
    });
    named.back.elements = named.back.elements.map((el) => {
      const name = el.name?.trim() || printCardElementDefaultName(el);
      const next = name === el.name ? el : { ...el, name };
      return next.type === "logo" && next.align == null
        ? { ...next, align: "center" as const }
        : next;
    });
    this.document = named;
    this.selectedElementId = null;
    this.activeSide = "front";
  }

  selectElement(id: string | null): void {
    this.selectedElementId = id;
  }

  onElementContextMenu(event: {
    id: string;
    clientX: number;
    clientY: number;
  }): void {
    this.openElementContextMenu(event.id, event.clientX, event.clientY);
  }

  onLayerListContextMenu(event: MouseEvent, elementId: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.openElementContextMenu(elementId, event.clientX, event.clientY);
  }

  private openElementContextMenu(
    elementId: string,
    clientX: number,
    clientY: number,
  ): void {
    this.selectElement(elementId);
    this.contextMenuPosition = { x: clientX, y: clientY };
    const trigger = this.elementContextMenuTrigger;
    if (!trigger) {
      return;
    }
    if (trigger.menuOpen) {
      trigger.closeMenu();
    }
    queueMicrotask(() => {
      trigger.openMenu();
    });
  }

  setActiveSide(side: "front" | "back"): void {
    this.activeSide = side;
    this.selectedElementId = null;
  }

  setZoom(zoom: number): void {
    this.zoom = Math.min(3, Math.max(0.25, Math.round(zoom * 100) / 100));
  }

  beginInteraction(): void {
    if (this.interactionOpen) {
      return;
    }
    this.pushHistory();
    this.interactionOpen = true;
  }

  endInteraction(): void {
    if (!this.interactionOpen) {
      return;
    }
    this.interactionOpen = false;
    this.redoStack = [];
    this.queuePreview();
  }

  onAddElement(
    type: PrintCardElementType,
    variable?: string,
    pickDomainVariable = false,
  ): void {
    this.pushHistory();
    const page = this.document.page;
    const zIndex =
      this.activeSideDesign.elements.reduce(
        (max, el) => Math.max(max, el.zIndex),
        0,
      ) + 1;
    const textVariable = pickDomainVariable
      ? undefined
      : (variable ?? "custom");
    const kind = type === "text" ? `text:${textVariable ?? "variable"}` : type;
    const sameCount = this.activeSideDesign.elements.filter((el) => {
      const elKind =
        el.type === "text"
          ? `text:${el.variable?.trim() || "variable"}`
          : el.type;
      return elKind === kind;
    }).length;
    const offset = Math.min(sameCount * 0.4, 3.2);
    const sideTextColor =
      this.activeSide === "front" ? this.frontTextColor : this.backTextColor;
    const sideNfcColor =
      this.activeSide === "front" ? this.frontNfcColor : this.backNfcColor;
    let el: PrintCardElement;
    if (type === "text") {
      let fontSizePt = PRINT_CARD_DEFAULT_FONT_SIZE_BODY_PT;
      let fontWeight: PrintCardFontWeight = PRINT_CARD_DEFAULT_FONT_WEIGHT_BODY;
      let widthCm = 4;
      const heightCm = printCardDefaultTextHeightCm(textVariable);
      let text: string | undefined;
      if (textVariable === "userName") {
        fontSizePt = PRINT_CARD_DEFAULT_FONT_SIZE_NAME_PT;
        fontWeight = PRINT_CARD_DEFAULT_FONT_WEIGHT_NAME;
      } else if (textVariable === "jobTitle") {
        fontSizePt = PRINT_CARD_DEFAULT_FONT_SIZE_TITLE_PT;
        fontWeight = PRINT_CARD_DEFAULT_FONT_WEIGHT_TITLE;
      } else if (textVariable === "companyName") {
        fontSizePt = PRINT_CARD_DEFAULT_FONT_SIZE_COMPANY_PT;
        fontWeight = PRINT_CARD_DEFAULT_FONT_WEIGHT_COMPANY;
      } else if (textVariable === "tagline") {
        fontSizePt = PRINT_CARD_DEFAULT_FONT_SIZE_TAGLINE_PT;
        fontWeight = PRINT_CARD_DEFAULT_FONT_WEIGHT_TAGLINE;
        widthCm = Math.min(6, page.widthCm - 1);
        text = PRINT_CARD_DEFAULT_TAGLINE_TEXT;
      } else if (
        textVariable === "phone" ||
        textVariable === "email" ||
        textVariable === "companyAddress"
      ) {
        fontSizePt = PRINT_CARD_DEFAULT_FONT_SIZE_BODY_PT;
        fontWeight = PRINT_CARD_DEFAULT_FONT_WEIGHT_BODY;
      }
      el = {
        id: crypto.randomUUID(),
        type: "text",
        xCm: 0.5 + offset,
        yCm: 1.5 + offset,
        widthCm,
        heightCm,
        zIndex,
        ...(textVariable ? { variable: textVariable } : {}),
        text,
        fontSizePt,
        fontWeight,
        color: sideTextColor,
        align: "left",
        textFit: "none",
      };
    } else if (type === "logo") {
      el = {
        id: crypto.randomUUID(),
        type: "logo",
        xCm: 0.5 + offset,
        yCm: 0.5 + offset,
        widthCm: PRINT_CARD_DEFAULT_LOGO_MAX_WIDTH_CM,
        heightCm: PRINT_CARD_DEFAULT_LOGO_MAX_HEIGHT_CM,
        zIndex,
        srcVariable: "companyImageUrl",
        align: this.activeSide === "back" ? "center" : "left",
      };
    } else if (type === "image") {
      el = {
        id: crypto.randomUUID(),
        type: "image",
        xCm: 0.5 + offset,
        yCm: 0.5 + offset,
        widthCm: 2.2,
        heightCm: 0.9,
        zIndex,
        srcUrl: "",
        align: "left",
      };
    } else if (type === "qr") {
      el = {
        id: crypto.randomUUID(),
        type: "qr",
        xCm: page.widthCm - PRINT_CARD_DEFAULT_QR_SIZE_CM - 0.5 - offset,
        yCm: page.heightCm - PRINT_CARD_DEFAULT_QR_SIZE_CM - 0.5 - offset,
        widthCm: PRINT_CARD_DEFAULT_QR_SIZE_CM,
        heightCm: PRINT_CARD_DEFAULT_QR_SIZE_CM,
        zIndex,
        srcVariable: "QRdigitalCardUrl",
      };
    } else if (type === "nfc") {
      el = {
        id: crypto.randomUUID(),
        type: "nfc",
        xCm: page.widthCm - PRINT_CARD_DEFAULT_NFC_SIZE_CM - 0.5 - offset,
        yCm: page.heightCm / 2 - PRINT_CARD_DEFAULT_NFC_SIZE_CM / 2 + offset,
        widthCm: PRINT_CARD_DEFAULT_NFC_SIZE_CM,
        heightCm: PRINT_CARD_DEFAULT_NFC_SIZE_CM,
        zIndex,
        srcVariable: "nfcIcon",
        color: sideNfcColor,
      };
    } else {
      el = {
        id: crypto.randomUUID(),
        type: "shape",
        xCm: 1 + offset,
        yCm: 1 + offset,
        widthCm: 2,
        heightCm: 1,
        zIndex,
        fill: "#EEEEEE",
      };
    }
    const clamped = clampPrintCardElementBox(
      el,
      this.document.page,
      el.type === "qr" || el.type === "nfc",
    );
    el = {
      ...el,
      widthCm: clamped.widthCm,
      heightCm: clamped.heightCm,
      xCm: clamped.xCm,
      yCm: clamped.yCm,
      name: el.name?.trim() || printCardElementDefaultName(el),
    };
    const existingNames = this.activeSideDesign.elements.map(
      (item) => item.name?.trim() || printCardElementDefaultName(item),
    );
    el = {
      ...el,
      name: nextUniquePrintCardElementName(
        el.name?.trim() || printCardElementDefaultName(el),
        existingNames,
      ),
    };
    let next = this.clone(this.document);
    const side = this.activeSide === "front" ? next.front : next.back;
    side.elements.push(el);
    next = addPrintCardElementPositionToAllSizeLayouts(
      next,
      el,
      this.activeSide,
    );
    this.document = next;
    this.selectedElementId = el.id;
    this.redoStack = [];
    this.queuePreview();
  }

  onBringToFront(): void {
    this.moveSelectedLayerBy(1);
  }

  onSendToBack(): void {
    this.moveSelectedLayerBy(-1);
  }

  onLayerDrop(event: CdkDragDrop<PrintCardElement[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const topToBottom = [...this.layerList];
    moveItemInArray(topToBottom, event.previousIndex, event.currentIndex);
    this.applyLayerOrder(topToBottom.slice().reverse());
  }

  private moveSelectedLayerBy(delta: 1 | -1): void {
    const el = this.selectedElement;
    if (!el) {
      return;
    }
    const bottomToTop = [...this.activeSideDesign.elements].sort(
      (a, b) => a.zIndex - b.zIndex,
    );
    const index = bottomToTop.findIndex((item) => item.id === el.id);
    if (index < 0) {
      return;
    }
    const target = index + delta;
    if (target < 0 || target >= bottomToTop.length) {
      return;
    }
    const nextOrder = [...bottomToTop];
    const [moved] = nextOrder.splice(index, 1);
    nextOrder.splice(target, 0, moved);
    this.applyLayerOrder(nextOrder);
  }

  private applyLayerOrder(bottomToTop: PrintCardElement[]): void {
    this.pushHistory();
    const next = this.clone(this.document);
    const side = this.activeSide === "front" ? next.front : next.back;
    const zById = new Map(
      bottomToTop.map((item, index) => [item.id, index + 1] as const),
    );
    side.elements = side.elements.map((item) => {
      const zIndex = zById.get(item.id);
      return zIndex === undefined || zIndex === item.zIndex
        ? item
        : { ...item, zIndex };
    });
    this.document = next;
    this.redoStack = [];
    this.queuePreview();
  }

  onMove(event: { id: string; xCm: number; yCm: number }): void {
    if (this.selectedElementId !== event.id) {
      this.selectedElementId = event.id;
    }
    this.patchSelected({ xCm: event.xCm, yCm: event.yCm }, false);
  }

  onResize(event: {
    id: string;
    xCm: number;
    yCm: number;
    widthCm: number;
    heightCm: number;
  }): void {
    if (this.selectedElementId !== event.id) {
      this.selectedElementId = event.id;
    }
    this.patchSelected(
      {
        xCm: event.xCm,
        yCm: event.yCm,
        widthCm: event.widthCm,
        heightCm: event.heightCm,
      },
      false,
    );
  }

  onRotate(event: {
    id: string;
    rotationDeg: number;
    xCm?: number;
    yCm?: number;
  }): void {
    if (this.selectedElementId !== event.id) {
      this.selectedElementId = event.id;
    }
    this.patchSelected(
      {
        rotationDeg: event.rotationDeg === 0 ? undefined : event.rotationDeg,
        ...(event.xCm != null ? { xCm: event.xCm } : {}),
        ...(event.yCm != null ? { yCm: event.yCm } : {}),
      },
      false,
    );
  }

  onPatchElement(patch: Partial<PrintCardElement>): void {
    this.patchSelected(patch, true);
    this.queuePreview();
  }

  onBackgroundColorChange(color: string): void {
    this.pushHistory();
    const next = this.clone(this.document);
    if (this.activeSide === "front") {
      next.front.backgroundColor = color;
      this.frontBackgroundColor = color;
    } else {
      next.back.backgroundColor = color;
      this.backBackgroundColor = color;
    }
    this.document = syncPrintCardActiveSizeLayout(next);
    this.redoStack = [];
    this.queuePreview();
  }

  onDeleteElement(): void {
    const el = this.selectedElement;
    if (!el) {
      return;
    }
    this.deleteElementById(el.id);
  }

  onDeleteLayerElement(elementId: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.deleteElementById(elementId);
  }

  private deleteElementById(elementId: string): void {
    const side =
      this.activeSide === "front" ? this.document.front : this.document.back;
    if (!side.elements.some((item) => item.id === elementId)) {
      return;
    }
    this.pushHistory();
    let next = this.clone(this.document);
    const nextSide = this.activeSide === "front" ? next.front : next.back;
    nextSide.elements = nextSide.elements.filter(
      (item) => item.id !== elementId,
    );
    next = removePrintCardElementFromSizeLayouts(next, elementId);
    this.document = next;
    if (this.selectedElementId === elementId) {
      this.selectedElementId = null;
    }
    this.redoStack = [];
    this.queuePreview();
  }

  onDuplicateElement(): void {
    const el = this.selectedElement;
    if (!el) {
      return;
    }
    this.pushHistory();
    const side = this.activeSideDesign;
    const maxZ = side.elements.reduce(
      (max, item) => Math.max(max, item.zIndex),
      0,
    );
    const existingNames = side.elements.map(
      (item) => item.name?.trim() || printCardElementDefaultName(item),
    );
    const clamped = clampPrintCardElementBox(
      {
        ...el,
        xCm: el.xCm + 0.2,
        yCm: el.yCm + 0.2,
      },
      this.document.page,
      el.type === "qr" || el.type === "nfc",
    );
    const namedCopy: PrintCardElement = {
      ...this.clone(el),
      id: crypto.randomUUID(),
      widthCm: clamped.widthCm,
      heightCm: clamped.heightCm,
      xCm: clamped.xCm,
      yCm: clamped.yCm,
      zIndex: maxZ + 1,
      name: nextUniquePrintCardElementName(
        el.name?.trim() || printCardElementDefaultName(el),
        existingNames,
      ),
    };
    let next = this.clone(this.document);
    const nextSide = this.activeSide === "front" ? next.front : next.back;
    nextSide.elements.push(namedCopy);
    next = addPrintCardElementPositionToAllSizeLayouts(
      next,
      namedCopy,
      this.activeSide,
    );
    this.document = next;
    this.selectedElementId = namedCopy.id;
    this.redoStack = [];
    this.queuePreview();
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  undo(): void {
    if (!this.undoStack.length) {
      return;
    }
    this.redoStack.push(this.clone(this.document));
    this.document = this.undoStack.pop()!;
    this.form.patchValue(
      {
        orientation:
          this.document.orientation === "portrait" ? "portrait" : "landscape",
      },
      { emitEvent: false },
    );
    this.selectedElementId = null;
    this.queuePreview();
  }

  redo(): void {
    if (!this.redoStack.length) {
      return;
    }
    this.undoStack.push(this.clone(this.document));
    this.document = this.redoStack.pop()!;
    this.form.patchValue(
      {
        orientation:
          this.document.orientation === "portrait" ? "portrait" : "landscape",
      },
      { emitEvent: false },
    );
    this.selectedElementId = null;
    this.queuePreview();
  }

  onSquashWithElement(partnerId: string): void {
    const primary = this.selectedElement;
    if (!primary || primary.type !== "text" || !partnerId.trim()) {
      return;
    }
    const partner = this.activeSideDesign.elements.find(
      (el) => el.id === partnerId,
    );
    if (!partner || partner.type !== "text" || partner.id === primary.id) {
      return;
    }
    this.pushHistory();
    const parts = [
      ...printCardTextElementToSquashParts(primary),
      ...printCardTextElementToSquashParts(partner),
    ];
    const separator =
      typeof primary.squashSeparator === "string"
        ? primary.squashSeparator
        : PRINT_CARD_DEFAULT_SQUASH_SEPARATOR;
    const separatorStyle = primary.squashSeparatorStyle;
    let next = this.clone(this.document);
    const nextSide = this.activeSide === "front" ? next.front : next.back;
    const idx = nextSide.elements.findIndex((item) => item.id === primary.id);
    if (idx < 0) {
      return;
    }
    const merged: PrintCardTextElement = {
      ...nextSide.elements[idx],
      type: "text",
      variable: "squash",
      squashParts: parts,
      squashSeparator: separator,
      squashSeparatorStyle: separatorStyle,
      text: undefined,
      fontSizePt: undefined,
      fontWeight: undefined,
      italic: undefined,
      uppercase: undefined,
      color: undefined,
      lineHeight: undefined,
      name:
        nextSide.elements[idx].name?.trim() ||
        printCardElementDefaultName({
          type: "text",
          variable: "squash",
          squashParts: parts,
        }),
    };
    nextSide.elements[idx] = merged;
    nextSide.elements = nextSide.elements.filter(
      (item) => item.id !== partner.id,
    );
    next = removePrintCardElementFromSizeLayouts(next, partner.id);
    this.document = next;
    this.selectedElementId = merged.id;
    this.redoStack = [];
    this.queuePreview();
  }

  onUnsquashElement(): void {
    const primary = this.selectedElement;
    if (!primary || primary.type !== "text") {
      return;
    }
    const parts = printCardTextElementToSquashParts(primary);
    if (parts.length < 2) {
      return;
    }
    this.pushHistory();
    const [first, ...rest] = parts;
    let next = this.clone(this.document);
    const nextSide = this.activeSide === "front" ? next.front : next.back;
    const idx = nextSide.elements.findIndex((item) => item.id === primary.id);
    if (idx < 0) {
      return;
    }
    const base = nextSide.elements[idx] as PrintCardTextElement;
    const firstEl: PrintCardTextElement = {
      ...base,
      variable: first.variable,
      squashParts: undefined,
      squashSeparator: undefined,
      squashSeparatorStyle: undefined,
      text:
        first.variable === "custom" || first.variable === "tagline"
          ? first.text
          : undefined,
      fontSizePt: first.fontSizePt,
      fontWeight: first.fontWeight,
      italic: first.italic,
      uppercase: first.uppercase,
      color: first.color,
      lineHeight: first.lineHeight,
      name: printCardElementDefaultName({
        type: "text",
        variable: first.variable,
      }),
    };
    nextSide.elements[idx] = firstEl;
    let maxZ = nextSide.elements.reduce(
      (max, item) => Math.max(max, item.zIndex),
      0,
    );
    const existingNames = nextSide.elements.map(
      (item) => item.name?.trim() || printCardElementDefaultName(item),
    );
    const createdElements: PrintCardTextElement[] = [];
    for (let partIndex = 0; partIndex < rest.length; partIndex += 1) {
      const part = rest[partIndex];
      maxZ += 1;
      const name = nextUniquePrintCardElementName(
        printCardElementDefaultName({
          type: "text",
          variable: part.variable,
        }),
        existingNames,
      );
      const created: PrintCardTextElement = {
        id: crypto.randomUUID(),
        type: "text",
        xCm: base.xCm,
        yCm: base.yCm + (partIndex + 1) * 0.35,
        widthCm: base.widthCm,
        heightCm: printCardDefaultTextHeightCm(part.variable),
        zIndex: maxZ,
        variable: part.variable,
        text:
          part.variable === "custom" || part.variable === "tagline"
            ? part.text
            : undefined,
        fontSizePt: part.fontSizePt,
        fontWeight: part.fontWeight,
        italic: part.italic,
        uppercase: part.uppercase,
        color: part.color,
        lineHeight: part.lineHeight,
        align: base.align,
        valign: base.valign,
        textFit: base.textFit,
        maxLines: base.maxLines,
        minFontSizePt: base.minFontSizePt,
        name,
      };
      const clamped = clampPrintCardElementBox(created, this.document.page);
      created.xCm = clamped.xCm;
      created.yCm = clamped.yCm;
      created.widthCm = clamped.widthCm;
      created.heightCm = clamped.heightCm;
      nextSide.elements.push(created);
      createdElements.push(created);
      existingNames.push(name);
    }
    for (const created of createdElements) {
      next = addPrintCardElementPositionToAllSizeLayouts(
        next,
        created,
        this.activeSide,
      );
    }
    this.document = next;
    this.selectedElementId = firstEl.id;
    this.redoStack = [];
    this.queuePreview();
  }

  private patchSelected(
    patch: Partial<PrintCardElement>,
    history: boolean,
  ): void {
    const el = this.selectedElement;
    if (!el) {
      return;
    }
    if (history) {
      this.pushHistory();
    }
    const next = this.clone(this.document);
    const side = this.activeSide === "front" ? next.front : next.back;
    const idx = side.elements.findIndex((item) => item.id === el.id);
    if (idx < 0) {
      return;
    }
    const previous = side.elements[idx];
    const merged = { ...previous, ...patch } as PrintCardElement;
    if (
      "xCm" in patch ||
      "yCm" in patch ||
      "widthCm" in patch ||
      "heightCm" in patch ||
      "rotationDeg" in patch
    ) {
      const square = merged.type === "qr" || merged.type === "nfc";
      const clamped = clampPrintCardElementBox(
        merged,
        this.document.page,
        square,
      );
      merged.widthCm = clamped.widthCm;
      merged.heightCm = clamped.heightCm;
      merged.xCm = clamped.xCm;
      merged.yCm = clamped.yCm;
    }
    side.elements[idx] = merged;
    if (merged.xCm !== previous.xCm || merged.yCm !== previous.yCm) {
      this.document = updatePrintCardElementPositionsInSizeLayouts(
        next,
        merged.id,
        { xCm: merged.xCm, yCm: merged.yCm },
        this.activeSide,
      );
    } else {
      this.document = next;
    }
    if (history) {
      this.redoStack = [];
    }
  }

  private pushHistory(): void {
    this.undoStack.push(this.clone(this.document));
    if (this.undoStack.length > MAX_HISTORY) {
      this.undoStack.shift();
    }
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
  }

  @HostListener("window:keydown", ["$event"])
  onKeydown(event: KeyboardEvent): void {
    if (this.showLayoutStart) {
      return;
    }
    const meta = event.metaKey || event.ctrlKey;
    const tag = (event.target as HTMLElement)?.tagName;
    const inField = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
    if (meta && event.key.toLowerCase() === "z") {
      event.preventDefault();
      if (event.shiftKey) {
        this.redo();
      } else {
        this.undo();
      }
      return;
    }
    if (!inField && meta && (event.key === "=" || event.key === "+")) {
      event.preventDefault();
      this.setZoom(this.zoom + 0.1);
      return;
    }
    if (!inField && meta && event.key === "-") {
      event.preventDefault();
      this.setZoom(this.zoom - 0.1);
      return;
    }
    if (!inField && meta && event.key === "0") {
      event.preventDefault();
      this.setZoom(1);
      return;
    }
    if (event.key === "Delete" || event.key === "Backspace") {
      if (inField) {
        return;
      }
      event.preventDefault();
      this.onDeleteElement();
      return;
    }
    if (
      event.key === "ArrowLeft" ||
      event.key === "ArrowRight" ||
      event.key === "ArrowUp" ||
      event.key === "ArrowDown"
    ) {
      const el = this.selectedElement;
      if (!el || inField) {
        return;
      }
      event.preventDefault();
      const step = event.shiftKey ? 0.25 : 0.05;
      let x = el.xCm;
      let y = el.yCm;
      if (event.key === "ArrowLeft") {
        x -= step;
      }
      if (event.key === "ArrowRight") {
        x += step;
      }
      if (event.key === "ArrowUp") {
        y -= step;
      }
      if (event.key === "ArrowDown") {
        y += step;
      }
      this.patchSelected(
        {
          xCm: Math.round(x * 100) / 100,
          yCm: Math.round(y * 100) / 100,
        },
        true,
      );
    }
  }

  save(): void {
    if (!this.layoutApplied || this.saving) {
      return;
    }
    if (!this.form.controls.name.value.trim()) {
      this.showNameInputDialog();
      return;
    }
    this.performSave();
  }

  private showNameInputDialog(): void {
    const dialogRef = this.dialog.open(NameInputDialogComponent, {
      width: "500px",
      data: {
        title: this.translate.instant("printCards.enterName") as string,
        placeholder: this.translate.instant("footers.name") as string,
        currentName: this.form.controls.name.value || "",
        actionType: "save" as const,
        saveButtonText: this.translate.instant("save") as string,
      },
    });
    dialogRef
      .afterClosed()
      .subscribe((result: { name?: string; action?: string } | undefined) => {
        if (!result?.name?.trim()) {
          return;
        }
        this.form.controls.name.setValue(result.name.trim());
        this.performSave();
      });
  }

  private performSave(): void {
    if (!this.canSave) {
      return;
    }
    const allowedSizes =
      this.form.controls.allowedPageSizePresetIds.value.length > 0
        ? this.form.controls.allowedPageSizePresetIds.value
        : this.orderPageSizePresets.map((item) => item.id);
    const orientation = this.selectedOrientation;
    const designDocument = ensurePrintCardSizeLayouts(
      syncPrintCardActiveSizeLayout(
        { ...this.document, orientation },
        this.selectedPageSizePresetId,
      ),
      this.orderPageSizePresets.filter((item) =>
        allowedSizes.includes(item.id),
      ),
      this.selectedPageSizePresetId,
    );
    const compiled = compilePrintCardDesign(
      designDocument,
      this.selectedPageSizePresetId,
    );
    const frontContent = compiled.frontContent;
    const backContent = compiled.backContent;
    const font = this.selectedFont;
    let quantities: number[];
    if (this.selectedTemplateType === "PLASTIC_NFC") {
      quantities = [
        ...new Set(
          this.form.controls.allowedQuantities.value
            .map((quantity) => Math.floor(Number(quantity)))
            .filter(
              (quantity) =>
                Number.isInteger(quantity) &&
                quantity !== this.plasticQuantityAddSentinel &&
                quantity >= PRINT_CARD_PLASTIC_QUANTITY_MIN &&
                quantity <= PRINT_CARD_PLASTIC_QUANTITY_MAX,
            ),
        ),
      ]
        .sort((a, b) => a - b)
        .slice(0, PRINT_CARD_ALLOWED_QUANTITIES_MAX);
      if (quantities.length === 0) {
        this.toastr.error(
          this.translate.instant("printCards.allowedQuantitiesLabel") as string,
        );
        return;
      }
    } else {
      const quantityCatalog = this.availableQuantityOptions;
      const filtered = [
        ...new Set(
          this.form.controls.allowedQuantities.value
            .map((quantity) => Math.floor(Number(quantity)))
            .filter((quantity) => quantityCatalog.includes(quantity)),
        ),
      ].sort((a, b) => a - b);
      quantities = filtered.length > 0 ? filtered : [...quantityCatalog];
    }
    const contentsByPageSizePresetId = compilePrintCardDesignForPageSizes(
      designDocument,
      allowedSizes,
    );
    this.saving = true;
    const payload = {
      type: this.selectedTemplateType,
      name: this.form.controls.name.value.trim() || "",
      frontContent,
      backContent,
      digitalCardProfileId:
        this.form.controls.digitalCardProfileId.value?.trim() || null,
      settings: {
        designVersion: 4,
        designDocument,
        contentsByPageSizePresetId,
        orientation,
        layoutKey: designDocument.layoutKey ?? null,
        layoutFrontVariantKey: designDocument.layoutFrontVariantKey ?? null,
        layoutBackVariantKey: designDocument.layoutBackVariantKey ?? null,
        frontBackgroundColor: designDocument.front.backgroundColor,
        backBackgroundColor: designDocument.back.backgroundColor,
        frontBackgroundImageUrl:
          designDocument.front.backgroundImageUrl ?? null,
        backBackgroundImageUrl: designDocument.back.backgroundImageUrl ?? null,
        frontTextColor: this.frontTextColor,
        backTextColor: this.backTextColor,
        frontNfcColor: this.frontNfcColor,
        backNfcColor: this.backNfcColor,
        allowedPageSizePresetIds: allowedSizes,
        allowedQuantities: quantities,
        fontName: font.fontName,
        fontFamily: font.fontFamily,
        fontCssUrl: font.fontCssUrl,
        ...(this.printerSettings
          ? {
              printerPaperId: this.form.controls.printerPaperId.value,
              printSides: this.form.controls.printSides.value,
              ...(this.printerSettings.spacerColors?.length
                ? {
                    printerSpacerColorId:
                      this.form.controls.printerSpacerColorId.value,
                  }
                : {}),
            }
          : {}),
      },
    };
    const req$ = this.printCardTemplateId
      ? this.printCardService.updateTemplate(this.printCardTemplateId, payload)
      : this.printCardService.createTemplate(payload);
    req$.subscribe({
      next: () => {
        this.saving = false;
        this.toastr.success(
          this.translate.instant(
            this.printCardTemplateId
              ? "printCards.templateUpdated"
              : "printCards.templateCreated",
          ) as string,
        );
        void this.router.navigate(["/print-cards"]);
      },
      error: () => {
        this.saving = false;
      },
    });
  }

  back(): void {
    void this.router.navigate(["/print-cards"]);
  }

  private loadCompanySettings(): void {
    this.companyService
      .get()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (company) => {
          this.companyFontFamily =
            (company?.brandFontFamily ?? "").trim() ||
            (company?.brandFontFileName ?? "").trim().replace(/\.[^.]+$/, "") ||
            "";
          this.companyFontFileUrl =
            (company?.brandFontFileUrl ?? "").trim() || null;
          this.companyPreviewName = (company?.name ?? "").trim();
          this.companyPreviewAddress = (company?.address ?? "").trim();
          this.companyPreviewImageUrl = (company?.photoUrl ?? "").trim();
          this.applyTemplateFontPreset();
        },
        error: () => {
          this.companyFontFamily = "";
          this.companyFontFileUrl = null;
          this.companyPreviewName = "";
          this.companyPreviewAddress = "";
          this.companyPreviewImageUrl = "";
        },
      });
  }

  private loadDomainVariables(): void {
    this.signaturesService
      .placeholders()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const placeholders =
            response &&
            typeof response === "object" &&
            "placeholders" in response &&
            Array.isArray((response as { placeholders?: unknown }).placeholders)
              ? ((response as { placeholders: string[] }).placeholders ?? [])
              : Array.isArray(response)
                ? (response as string[])
                : [];
          const names = new Set(this.domainVariables);
          for (const placeholder of placeholders) {
            const name = placeholder.replace("${", "").replace("}", "").trim();
            if (
              !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name) ||
              DOMAIN_VARIABLE_EXCLUDE.has(name) ||
              name.startsWith("addon") ||
              name.startsWith("label") ||
              name.startsWith("userThumbnail") ||
              name.startsWith("QRdigitalCard")
            ) {
              continue;
            }
            names.add(name);
          }
          this.domainVariables = [...names].sort((a, b) => a.localeCompare(b));
        },
      });
  }

  private applyTemplateFontPreset(): void {
    const pending = this.pendingFontSettings;
    if (!pending) {
      return;
    }
    const fontCssUrl = pending.fontCssUrl?.trim() || "";
    const fontFamily = pending.fontFamily?.trim() || "";
    const fontName = pending.fontName?.trim() || "";
    if (!fontCssUrl && this.companyBrandFontReady) {
      const companyFamily = this.companyFontFamily.trim().toLowerCase();
      const familyPrimary = fontFamily.replace(/^["']|["'].*$/g, "").trim();
      if (
        !fontFamily ||
        familyPrimary.toLowerCase() === companyFamily ||
        fontName.toLowerCase() === companyFamily
      ) {
        this.form.patchValue({ fontPreset: BRAND_FONT_PRESET_ID });
        this.pendingFontSettings = null;
        return;
      }
    }
    const match =
      this.builtInFontPresets.find(
        (font) =>
          (fontCssUrl && font.fontCssUrl === fontCssUrl) ||
          (fontFamily && font.fontFamily === fontFamily),
      ) ?? null;
    if (match) {
      this.form.patchValue({ fontPreset: match.id });
      this.pendingFontSettings = null;
    }
  }
}
